import Swup from 'swup';
import SwupHeadPlugin from '@swup/head-plugin';
import SwupBodyClassPlugin from '@swup/body-class-plugin';
import SwupA11yPlugin from '@swup/a11y-plugin';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { initTextEffects, resetTextEffects } from './widgets/effects.js';
import { initCantineSwiper } from './widgets/cantSwiper.js';
import { initGallerySwiper } from './widgets/gallerySwiper.js';
import { initCantScroll, setDisableAutoScroll } from './widgets/cantScroll.js';
import { initFocusModal } from './widgets/focusModal.js';
import { mouseMove, startCursorLoading, stopCursorLoading, isCursorLoading } from './widgets/cursor.js';
import { initArchiveCantine } from './widgets/allCant.js';
import { initGalleryScroll } from './widgets/galleryScroll.js';
import {
  initAllParallaxWidgets,
  destroyAllParallaxWidgets,
  debugAllParallaxWidgets,
  debugParallaxWidgetsByType,
  initAllHomeParallaxWidgets,
  initAllImageParallaxWidgets,
  destroyAllImageParallaxWidgets,
  initAllCardParallaxWidgets,
  destroyAllCardParallaxWidgets
} from './widgets/parallax.js';
import { initHamburgerMenu } from './widgets/hamburgerMenu.js';
import { initSimpleScrollVideo, registerScrollMapPins, renderPins, updatePinPositions } from './widgets/simpleScrollVideo.js';
import { initFooterMarquee } from './widgets/footerMarquee.js';
import { initGroupSwiper } from './widgets/groupSwiper.js';

let lastTransitionId = null;
let isFirstPageLoad = true;
let customOverlayParams = null;
let isTransitioning = false; // Flag per tracciare lo stato della transizione
let scrollPosition = 0; // Memorizza la posizione dello scroll
const PRELOADER_BODY_SCROLL_PROPS = ['position', 'top', 'left', 'right', 'width', 'overflow', 'paddingRight', 'touchAction'];
const PRELOADER_ROOT_SCROLL_PROPS = ['overflow', 'height', 'overscrollBehavior'];
let preloaderScrollLockApplied = false;
let preloaderBodyStyles = {};
let preloaderRootStyles = {};

function setCustomOverlayParams(params) {
  customOverlayParams = params || null;
  window.customOverlayParams = customOverlayParams;
}

window.setCustomOverlayParams = setCustomOverlayParams;

// Smooth scroll helpers - sostituisce Lenis
function initSmoothScroll() {
  // Aggiunge scroll-behavior smooth al CSS
  document.documentElement.style.scrollBehavior = 'smooth';
}

function lockInitialScroll() {
  if (preloaderScrollLockApplied) return;

  const root = document.documentElement;
  preloaderBodyStyles = PRELOADER_BODY_SCROLL_PROPS.reduce((acc, prop) => {
    acc[prop] = document.body.style[prop];
    return acc;
  }, {});
  preloaderRootStyles = PRELOADER_ROOT_SCROLL_PROPS.reduce((acc, prop) => {
    acc[prop] = root.style[prop];
    return acc;
  }, {});

  const currentScroll = window.pageYOffset || document.documentElement.scrollTop || 0;
  document.body.dataset.preloaderScrollPosition = String(currentScroll);
  document.body.style.position = 'fixed';
  document.body.style.top = `-${currentScroll}px`;
  document.body.style.left = '0';
  document.body.style.right = '0';
  document.body.style.width = '100%';
  document.body.style.overflow = 'hidden';
  document.body.style.touchAction = 'none';

  const scrollbarWidth = window.innerWidth - root.clientWidth;
  if (scrollbarWidth > 0) {
    document.body.style.paddingRight = `${scrollbarWidth}px`;
  }

  root.style.overflow = 'hidden';
  root.style.height = '100%';
  root.style.overscrollBehavior = 'none';

  preloaderScrollLockApplied = true;
}

function unlockInitialScroll() {
  if (!preloaderScrollLockApplied) return;

  const root = document.documentElement;
  const storedScroll = document.body.dataset.preloaderScrollPosition;

  PRELOADER_BODY_SCROLL_PROPS.forEach((prop) => {
    document.body.style[prop] = preloaderBodyStyles[prop] || '';
  });

  PRELOADER_ROOT_SCROLL_PROPS.forEach((prop) => {
    root.style[prop] = preloaderRootStyles[prop] || '';
  });

  delete document.body.dataset.preloaderScrollPosition;

  const targetScroll = storedScroll ? parseInt(storedScroll, 10) : 0;
  if (!Number.isNaN(targetScroll)) {
    window.scrollTo(0, targetScroll);
  }

  preloaderBodyStyles = {};
  preloaderRootStyles = {};
  preloaderScrollLockApplied = false;
}

function scrollTo(target, options = {}) {
  const { immediate = false, duration = 0 } = options;

  if (typeof target === 'number') {
    // Scroll verticale
    if (immediate) {
      window.scrollTo(0, target);
    } else {
      window.scrollTo({ top: target, behavior: 'smooth' });
    }
  } else if (target && typeof target.scrollIntoView === 'function') {
    // Elemento DOM
    if (immediate) {
      window.scrollTo(0, target.offsetTop);
    } else {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }
}

function disableScroll() {
  // Memorizza la posizione corrente dello scroll
  scrollPosition = window.pageYOffset || document.documentElement.scrollTop;

  // Blocco aggressivo dello scroll
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.top = `-${scrollPosition}px`;
  document.body.style.width = '100%';
  document.documentElement.style.overflow = 'hidden';

  // Previeni wheel/touch events
  document.addEventListener('wheel', preventScroll, { passive: false });
  document.addEventListener('touchmove', preventScroll, { passive: false });

  //console.log('🚫 Scroll BLOCCATO - posizione salvata:', scrollPosition);
}

function enableScroll() {
  // Ripristina lo scroll
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.top = '';
  document.body.style.width = '';
  document.documentElement.style.overflow = '';

  // Rimuovi i listener degli eventi
  document.removeEventListener('wheel', preventScroll, { passive: false });
  document.removeEventListener('touchmove', preventScroll, { passive: false });

  // Ripristina la posizione dello scroll (solo se non siamo in una transizione)
  if (!isTransitioning && scrollPosition > 0) {
    window.scrollTo(0, scrollPosition);
  }

  //console.log('✅ Scroll RIATTIVATO');
}

// Funzione helper per prevenire lo scroll durante le transizioni
function preventScroll(e) {
  if (isTransitioning) {
    e.preventDefault();
    e.stopPropagation();
    return false;
  }
}

// Helper functions per verificare URL cantine (supporta /cantine/ e /wineries/)
function isCantinaUrl(url) {
  if (!url) return false;
  return url.includes('/cantine/') || url.includes('/wineries/');
}

function isCantinaSingola(url) {
  if (!url) return false;
  // Verifica se è una cantina singola (non archivio)
  // Escludi gli URL che terminano con /cantine/ o /wineries/ (archivio)
  const isArchive = /\/cantine\/?$/.test(url) || /\/wineries\/?$/.test(url);
  return isCantinaUrl(url) && !isArchive;
}

function isCantinaArchive(url) {
  if (!url) return false;
  return /\/cantine\/?$/.test(url) || /\/wineries\/?$/.test(url);
}

// Ritorna il colore della cantina corrente (fallback su dataset/rosso)
function getCantinaColorSafe(scope = document) {
  const tryGet = el =>
    el ? getComputedStyle(el).getPropertyValue('--cantina-color').trim() : '';

  const candidates = [
    scope.body,
    scope.querySelector('main.cantina-single'),
    scope.querySelector('.cantina-hero'),
  ];

  for (const el of candidates) {
    const v = tryGet(el);
    if (v) return v;
  }

  // fallback: prova dallo space-transition (nextBgColor) o usa rosso
  const datasetColor = scope.querySelector('.space-transition')?.dataset?.nextBgColor?.trim();
  return datasetColor || '#c7c0b7';
}

// Funzione helper per animare i pin con delay personalizzabile
function animatePinsWithDelay(delay = 4) {
  const pins = Array.from(document.querySelectorAll('.pin'));
  if (pins.length) {
    gsap.to(pins, {
      opacity: 1,
      duration: 0.8,
      stagger: 0.1,
      ease: 'power2.out',
      delay: delay
    });
  }
}

// Gestione video semplificata e universale
let videoElement = null;

function setupVideoForMobile() {
  //console.log('🎬 Inizializzazione video universale...');

  // Cerca prima il nuovo sistema, poi il vecchio
  videoElement = document.querySelector('.scroll-video') || document.querySelector('.italy-video');
  if (!videoElement) {
    //to (né .scroll-video né .italy-video)');
    return;
  }

  //console.log('✅ Video element trovato:', videoElement.className);

  // Verifica se il video è gestito da SimpleScrollVideo
  const isSimpleScrollVideo = videoElement.closest('[data-use-simple-video="true"]');
  if (isSimpleScrollVideo) {
    //console.log('⚠️ Video gestito da SimpleScrollVideo - autoplay disabilitato');
    // Solo imposta le proprietà base, NON fare autoplay
    videoElement.muted = true;
    videoElement.playsInline = true;
    return;
  }

  // Imposta le proprietà necessarie per autoplay
  videoElement.muted = true;
  videoElement.playsInline = true;

  // Tenta l'autoplay universale (desktop e mobile) solo per video non gestiti da SimpleScrollVideo
  tryAutoplay();
}

function tryAutoplay() {
  if (!videoElement) return;

  console.log('▶️ Tentativo autoplay universale...');

  videoElement.muted = true;
  videoElement.playsInline = true;

  videoElement.play()
    .then(() => {
      console.log('✅ Autoplay riuscito!');
    })
    .catch(err => {
      console.log('⚠️ Autoplay fallito:', err.name);
      // Non è più necessario fare nulla, il video partirà quando possibile
    });
}

function playVideoSafely() {
  if (!videoElement) {
    videoElement = document.querySelector('.scroll-video') || document.querySelector('.italy-video');
  }

  if (!videoElement) return;

  videoElement.muted = true;
  videoElement.playsInline = true;

  return videoElement.play().catch(err => {
    console.log('⚠️ Video play fallito:', err.name);
  });
}

// Preloader
function initPreloader() {
  const preloader = document.getElementById('initial-preloader');
  const logoSvg = document.getElementById('preloader-logo');

  if (!preloader || !logoSvg) {
    console.warn('⚠️ Preloader o logo non trovati');
    return;
  }

  lockInitialScroll();

  // Avvia direttamente l'animazione (il logo è già nell'HTML)
  startPreloaderAnimation();

  function startPreloaderAnimation() {
    // Seleziona il rettangolo del clipPath da animare
    const fillRect = document.getElementById('fillRect');

    if (!fillRect) {
      console.warn('⚠️ fillRect non trovato');
      unlockInitialScroll();
      return;
    }

    // Define GSAP timeline for preloader con effetto clipPath
    const tl = gsap.timeline({
      defaults: { ease: 'power2.out' },
      onComplete: () => {
        unlockInitialScroll();
        // Inizializza il video ma non tentare l'autoplay subito
        setupVideoForMobile();
        initTextEffects(0); // Initialize on initial load
      }
    });

    // Imposta lo stato iniziale del clipPath (larghezza 0)
    gsap.set(fillRect, {
      attr: { width: 0 }
    });
    gsap.set(preloader, { display: 'flex' });
    tl
      .to(preloader, { autoAlpha: 1, duration: 0.2 })
      // Pausa iniziale prima di iniziare il riempimento
      .to({}, { duration: 0.3 })
      // Anima il riempimento del logo da sinistra a destra espandendo il clipPath
      .to(fillRect, {
        attr: { width: 432.021 }, // Larghezza totale dell'SVG (viewBox)
        duration: 1.8,
        ease: 'power2.inOut',
        onUpdate: function () {
          // Log progress per debug (opzionale)
          const progress = this.progress() * 100;
          //if (progress % 20 < 1) { // Log ogni 20%
          //console.log(`🎨 Riempimento logo: ${Math.round(progress)}%`);
          //}
        },
        // onComplete: () => {
        //   console.log('✅ Riempimento logo completato - il preloader uscirà tra poco');
        // }
      })
      // Delay più lungo dopo il riempimento COMPLETO prima di far uscire il preloader
      .to(preloader, { yPercent: -100, duration: 0.6, delay: 0.8 })
      .set(preloader, { display: 'none' });

    // Aggiorna il theme-color per iOS Liquid Glass al primo caricamento
    updateThemeColorForCantina('preloader');
    preloader.classList.add('loading');

    // Avvia la timeline dopo un breve delay
    setTimeout(() => {
      tl.play();
    }, 500);
  }
}

// Modal
function initContactModal() {
  const modal = document.getElementById("contact-modal");
  const modalInfo = document.getElementById("modal-info");
  const closeBtn = modal.querySelector(".close");

  // 1) Setto il modal inizialmente nascosto con GSAP e disabilito le interazioni
  gsap.set(modal, { autoAlpha: 0, pointerEvents: "none" });

  // Funzione helper per rilevare la lingua corrente
  function getCurrentLang() {
    const lang = document.documentElement.lang || document.documentElement.getAttribute('lang');
    return lang && lang.startsWith('en') ? 'en' : 'it'; // Default a 'it' se non rilevato
  }

  // 2) Dati di contatto con traduzioni per italiano e inglese
  const contactData = {
    "email": {
      it: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" data-name="Layer 2" viewBox="0 0 48 48" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M6.47 10.71a2 2 0 0 0-2 2h0v22.61a2 2 0 0 0 2 2h35.06a2 2 0 0 0 2-2h0V12.68a2 2 0 0 0-2-2H6.47Zm33.21 3.82L24 26.07 8.32 14.53" style="fill:none;stroke-linecap:round;stroke-linejoin:round"/></svg>Scrivi una mail`,
        link: "mailto:info@agricolegussalliberetta.com"
      },
      en: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" data-name="Layer 2" viewBox="0 0 48 48" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M6.47 10.71a2 2 0 0 0-2 2h0v22.61a2 2 0 0 0 2 2h35.06a2 2 0 0 0 2-2h0V12.68a2 2 0 0 0-2-2H6.47Zm33.21 3.82L24 26.07 8.32 14.53" style="fill:none;stroke-linecap:round;stroke-linejoin:round"/></svg>Write an email`,
        link: "mailto:info@agricolegussalliberetta.com"
      }
    },
    "telefono": {
      it: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M19.308 12.467a24.095 24.095 0 0 1-.881-4.384C18.27 6.602 16.977 5.5 15.488 5.5H8.58c-1.777 0-3.145 1.535-2.989 3.304 1.575 17.829 15.777 32.03 33.606 33.606 1.77.156 3.304-1.207 3.304-2.984v-6.16c0-2.248-1.102-3.536-2.583-3.693a24.095 24.095 0 0 1-4.384-.88 4.903 4.903 0 0 0-4.87 1.243l-2.957 2.957a31.27 31.27 0 0 1-12.599-12.599l2.957-2.957a4.903 4.903 0 0 0 1.244-4.87Z" style="fill:none;stroke-linecap:round;stroke-linejoin:round"/></svg>Chiama`,
        link: "tel:+390306527218"
      },
      en: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 48 48" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M19.308 12.467a24.095 24.095 0 0 1-.881-4.384C18.27 6.602 16.977 5.5 15.488 5.5H8.58c-1.777 0-3.145 1.535-2.989 3.304 1.575 17.829 15.777 32.03 33.606 33.606 1.77.156 3.304-1.207 3.304-2.984v-6.16c0-2.248-1.102-3.536-2.583-3.693a24.095 24.095 0 0 1-4.384-.88 4.903 4.903 0 0 0-4.87 1.243l-2.957 2.957a31.27 31.27 0 0 1-12.599-12.599l2.957-2.957a4.903 4.903 0 0 0 1.244-4.87Z" style="fill:none;stroke-linecap:round;stroke-linejoin:round"/></svg>Call`,
        link: "tel:+390306527218"
      }
    },
    "indirizzo": {
      it: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M128 68a36 36 0 1 0 36 36 36.04 36.04 0 0 0-36-36Zm0 64a28 28 0 1 1 28-28 28.031 28.031 0 0 1-28 28Zm0-112a84.095 84.095 0 0 0-84 84c0 30.418 14.17 62.793 40.981 93.625a250.12 250.12 0 0 0 40.725 37.652 4 4 0 0 0 4.588 0 250.12 250.12 0 0 0 40.724-37.652C197.828 166.793 212 134.418 212 104a84.095 84.095 0 0 0-84-84Zm37.103 172.234A254.04 254.04 0 0 1 128 227.035a254.04 254.04 0 0 1-37.104-34.8C73.152 171.794 52 139.895 52 104a76 76 0 0 1 152 0c0 35.896-21.153 67.796-38.897 88.234Z"/></svg>Apri su Google Maps`,
        // URL generato per la ricerca di Via Costa, 2 – Monticelli Brusati (BS)
        link: "https://www.google.com/maps/search/?api=1&query=Via+Costa,+2+25040+Monticelli+Brusati+BS+Italy"
      },
      en: {
        value: `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 256 256" style="display: inline-block; vertical-align: middle; margin-right: 8px;"><path d="M128 68a36 36 0 1 0 36 36 36.04 36.04 0 0 0-36-36Zm0 64a28 28 0 1 1 28-28 28.031 28.031 0 0 1-28 28Zm0-112a84.095 84.095 0 0 0-84 84c0 30.418 14.17 62.793 40.981 93.625a250.12 250.12 0 0 0 40.725 37.652 4 4 0 0 0 4.588 0 250.12 250.12 0 0 0 40.724-37.652C197.828 166.793 212 134.418 212 104a84.095 84.095 0 0 0-84-84Zm37.103 172.234A254.04 254.04 0 0 1 128 227.035a254.04 254.04 0 0 1-37.104-34.8C73.152 171.794 52 139.895 52 104a76 76 0 0 1 152 0c0 35.896-21.153 67.796-38.897 88.234Z"/></svg>Open on Google Maps`,
        link: "https://www.google.com/maps/search/?api=1&query=Via+Costa,+2+25040+Monticelli+Brusati+BS+Italy"
      }
    }
  };

  // 3) Funzione "universale" per aprire il modal in base al tipo
  function openModal(type) {
    const typeData = contactData[type];
    if (!typeData) return;

    // Ottengo la lingua corrente
    const currentLang = getCurrentLang();
    const data = typeData[currentLang] || typeData['it']; // Fallback a italiano

    // Svuoto eventuali classi di stato
    modal.classList.remove("modal--email", "modal--telefono", "modal--indirizzo");

    // Creo il contenuto interno
    modalInfo.innerHTML = `
      <a href="${data.link}" target="_blank" class="modal-btn" style="text-decoration:none;">
        ${data.value}
      </a>
    `;
    // (il testo del link sarà solo l'email o solo il numero o "Apri su Google Maps"/"Open on Google Maps")

    modal.classList.add(`modal--${type}`);

    // Lancio l'animazione di apertura col GSAP
    gsap.to(modal, {
      duration: 0.4,
      autoAlpha: 1,
      pointerEvents: "auto",
      ease: "power3.out"
    });
  }

  // 4) Funzione per chiudere il modal con GSAP
  function closeModalAnim() {
    gsap.to(modal, {
      duration: 0.3,
      autoAlpha: 0,
      scale: 1,
      pointerEvents: "none",
      ease: "power3.in"
    });
    // Rimuovo le classi di stato dopo un breve ritardo
    setTimeout(() => {
      modal.classList.remove("modal--email", "modal--telefono", "modal--indirizzo");
      modalInfo.innerHTML = ""; // (opzionale: pulisco il contenuto)
    }, 400);
  }

  // 5) Eventi di chiusura: clic su [X] o clic fuori dal modal-content
  closeBtn.addEventListener("click", closeModalAnim);
  window.addEventListener("click", evt => {
    if (evt.target === modal) {
      closeModalAnim();
    }
  });

  // 6) Collego i trigger sui tuoi link in footer/info-container:
  // – Email
  document.querySelectorAll(".footer-links a[href^='mailto']").forEach(anchor => {
    anchor.addEventListener("click", evt => {
      evt.preventDefault();
      openModal("email");
    });
  });

  // – Telefono
  document.querySelectorAll(".footer-links a[href^='tel']").forEach(anchor => {
    anchor.addEventListener("click", evt => {
      evt.preventDefault();
      openModal("telefono");
    });
  });


  //   Qui uso il selettore "a.footer-links" in base al tuo HTML: 
  document.querySelectorAll("a.footer-links-address").forEach(anchor => {
    anchor.addEventListener("click", evt => {
      evt.preventDefault();
      openModal("indirizzo");
    });
  });

} // fine initContactModal

// Cursor
function initCursor() {
  mouseMove();
  // Assicurati che il cursor sia in uno stato pulito all'inizializzazione
  if (isCursorLoading()) {
    stopCursorLoading();
  }
}

// Contact Labels
function gestisciFloatingLabels() {
  var inputs = document.querySelectorAll('.floating-input');
  var labels = document.querySelectorAll('.floating-label');

  inputs.forEach(function (input, index) {
    var label = labels[index];

    // Controlla se l'input ha già un valore al caricamento della pagina
    if (input.value) {
      animateLabel(label, true);
    }

    input.addEventListener('focus', function () {
      animateLabel(label, true);
    });

    input.addEventListener('blur', function () {
      if (!input.value) {
        animateLabel(label, false);
      }
    });
  });
}

function animateLabel(label, isActive) {
  const isMobile = window.innerWidth <= 811;

  const fontSize = isMobile ? "12px" : "1.2rem";

  if (isActive) {
    gsap.to(label, {
      y: -23,
      fontSize: isMobile ? "10px" : "12px",
      duration: 0.2
    });
  } else {
    gsap.to(label, {
      y: 0,
      fontSize: fontSize,
      duration: 0.2
    });
  }
}

// Gestione specifica per i link delle cantine
function handleCantineLinks() {
  // Rimuovi eventuali listener precedenti per evitare duplicazioni
  if (window.cantineLinksHandler) {
    document.removeEventListener('click', window.cantineLinksHandler, { capture: true });
  }

  window.cantineLinksHandler = (e) => {
    // SKIP per selettori lingua WPML - navigazione gestita nativamente dal browser (escluso da Swup)
    if (e.target.closest('.header-lang-switcher, .wpml-ls-item, .wpml-ls-link, [class*="wpml-ls"]')) {
      return;
    }

    // Cerca link che portano alle cantine o sono/dentro elementi con classi specifiche
    // Cerca anche dentro .cantina-link-sw e .agricole-card-btn
    let link = e.target.closest('a[href*="/cantine/"], a[href*="/wineries/"]');

    // Se non trovato, cerca se abbiamo cliccato dentro un elemento con classe speciale
    if (!link) {
      const specialContainer = e.target.closest('.cantina-link-sw, .agricole-card-btn');
      if (specialContainer) {
        // Cerca un link dentro questo container
        link = specialContainer.querySelector('a[href]') || specialContainer.closest('a[href]');
      }
    }

    // Se il target stesso ha la classe speciale ed è un link
    if (!link && (e.target.classList.contains('cantina-link-sw') || e.target.classList.contains('agricole-card-btn'))) {
      link = e.target.closest('a[href]');
    }

    if (!link) return;

    const href = link.getAttribute('href');

    // console.log('🔍 handleCantineLinks - Link trovato:', {
    //   href,
    //   classList: link.className,
    //   isInSpecialContainer: !!e.target.closest('.cantina-link-sw, .agricole-card-btn')
    // });

    // Skip se è il link della pagina corrente o se è un link esterno assoluto
    if (!href || href === window.location.pathname) {
      return;
    }

    // Verifica che il link porti effettivamente a una cantina SINGOLA (non all'archivio)
    // Supporta sia /cantine/ che /wineries/
    if (!isCantinaSingola(href)) {
      //console.log('⚠️ Non è una cantina singola, skip');
      return; // Non è un link a una cantina singola (potrebbe essere archivio o altra pagina)
    }

    //console.log('✅ Link a cantina singola rilevato:', href);

    // Fix specifico per Chrome: verifica se il link è dentro un Swiper
    const isInSwiper = !!link.closest('.swiper, .swiper-container, .swiper-slide');
    const isInCantinaSwiper = !!link.closest('.cantine-swiper-container, .cantine-swiper');
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);

    if (isChrome && isInSwiper) {
      // console.log('🔍 Chrome: Link cantina in Swiper detected', {
      //   href,
      //   isInSwiper,
      //   isInCantinaSwiper,
      //   swiperContainer: link.closest('.swiper, .swiper-container, .swiper-slide')
      // });

      // Per Chrome, forziamo un piccolo delay per assicurarci che gli eventi siano processati correttamente
      setTimeout(() => {
        updateNavMetaFromAnchor(link);
      }, 10);
    }

    // Cerca i dati della cantina di destinazione da window.cantineData
    let cantineLogo = null;
    let cantinaBg = '#e4dcd3'; // Colore di default (fallback)
    let cantinaHeroBg = null; // Immagine hero_bg

    // console.log('🔍 Cercando dati cantina per href:', href);
    // console.log('📦 window.cantineData disponibile?', !!window.cantineData, 'Numero cantine:', window.cantineData?.length);

    // Prima prova a cercare i dati dalla variabile globale cantineData
    if (window.cantineData && Array.isArray(window.cantineData)) {
      const targetCantina = window.cantineData.find(c => {
        // Cerca corrispondenza per URL completo, slug o permalink
        // Supporta sia /cantine/ che /wineries/
        const match = href.includes(c.url) ||
          href.includes(c.slug) ||
          href.includes('/cantine/' + c.slug) ||
          href.includes('/wineries/' + c.slug);

        //console.log('🔎 Controllo cantina:', c.slug, 'Match:', match);
        return match;
      });

      if (targetCantina) {
        cantineLogo = targetCantina.logo;
        cantinaBg = targetCantina.colore;
        cantinaHeroBg = targetCantina.hero_bg; // Immagine hero
        // console.log('🎨 Dati cantina trovati:', {
        //   logo: cantineLogo,
        //   colore: cantinaBg,
        //   hero_bg: cantinaHeroBg,
        //   cantina: targetCantina.slug
        // });
      } else {
        console.warn('⚠️ Cantina non trovata in cantineData per:', href);
      }
    } else {
      console.warn('⚠️ window.cantineData non disponibile o non è array');
    }

    // Fallback: cerca il logo nel DOM se non trovato nei dati
    if (!cantineLogo) {
      // Cerca nell'elemento padre il logo specifico della cantina
      const slideText = link.closest('.slide-text');
      if (slideText) {
        const logoImg = slideText.querySelector('.slide-logo img');
        if (logoImg && logoImg.src) {
          cantineLogo = logoImg.src;
        }
      }

      // Fallback: cerca il logo in altri possibili contenitori
      if (!cantineLogo) {
        const logoImg = link.querySelector('img') || link.closest('div').querySelector('img');
        if (logoImg && logoImg.src) {
          cantineLogo = logoImg.src;
        }
      }
    }

    // Se non è un link esterno, prepara i parametri personalizzati per l'overlay
    if (!href.startsWith('http') || href.includes(window.location.hostname)) {
      e.preventDefault();

      // Memorizza i parametri personalizzati per l'intera transizione
      customOverlayParams = {
        logo: cantineLogo,
        bg: cantinaBg,
        bgImage: cantinaHeroBg, // Immagine hero_bg della cantina
        hideLogo: false // Le cantine mostrano il logo nell'overlay
      };

      // Sincronizza con la variabile globale
      window.customOverlayParams = customOverlayParams;

      // console.log('🏷️ Transizione cantina configurata:', customOverlayParams);
      // console.log('✅ Parametri finali:', {
      //   logo: customOverlayParams.logo ? 'Presente' : 'Assente',
      //   bg: customOverlayParams.bg,
      //   bgImage: customOverlayParams.bgImage ? 'Presente' : 'Assente',
      //   hideLogo: customOverlayParams.hideLogo
      // });

      // Avvia l'animazione del cursor solo se non è già attiva
      if (!isCursorLoading()) {
        startCursorLoading();
      }

      //console.log('🚀 Avvio navigazione Swup verso:', href);
      // Naviga con Swup (l'overlay userà i parametri personalizzati)
      swup.navigate(href);
    }
  };

  // Usa capture: true per intercettare PRIMA di Swup
  document.addEventListener('click', window.cantineLinksHandler, { capture: true });
}

// Gestione universale del cursor loading per TUTTI i link interni
function handleUniversalCursorLoading() {
  // Rimuovi eventuali listener precedenti
  if (window.universalCursorHandler) {
    document.removeEventListener('click', window.universalCursorHandler);
  }

  window.universalCursorHandler = (e) => {
    const link = e.target.closest('a');
    if (!link) return;

    // SKIP per selettori lingua WPML - navigazione normale senza cursor loading custom
    const isLanguageSwitcher = e.target.closest('.header-lang-switcher, .wpml-ls-item, .wpml-ls-link, [class*="wpml-ls"]');
    if (isLanguageSwitcher) {
      return;
    }

    const href = link.getAttribute('href');

    // Skip solo per link esterni, email, telefono, anchor
    if (!href ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('#') ||
      (href.startsWith('http') && !href.includes(window.location.hostname))) {
      return;
    }

    // Skip se è la stessa pagina
    if (href === window.location.pathname) {
      return;
    }

    // Se è un link interno, avvia SEMPRE l'animazione del cursor
    if (href.startsWith('/') || href.startsWith(window.location.origin) ||
      (!href.startsWith('http') && !href.includes('://'))) {

      const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
      const isInSwiper = !!link.closest('.swiper, .swiper-container, .swiper-slide');

      // Fix specifico per Chrome con Swiper
      if (isChrome && isInSwiper) {
        // Piccolo delay per Chrome quando il link è in uno Swiper
        setTimeout(() => {
          if (!isCursorLoading()) {
            startCursorLoading();
          }
        }, 15);
      } else {
        // Comportamento normale per altri browser o link non in Swiper
        if (!isCursorLoading()) {
          startCursorLoading();
        }
      }
    }
  };

  // Usa capture: true per catturare prima dei gestori specifici
  document.addEventListener('click', window.universalCursorHandler, { capture: true });
}

// Fix specifico per Chrome e Swiper
function handleChromeSwipersLinks() {
  const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
  if (!isChrome) return;

  // Rimuovi eventuali listener precedenti
  if (window.chromeSwiperLinksHandler) {
    document.removeEventListener('click', window.chromeSwiperLinksHandler);
  }

  window.chromeSwiperLinksHandler = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const isInCantinaSwiper = !!link.closest('.cantine-swiper-container, .cantine-swiper');
    if (!isInCantinaSwiper) return;

    const href = link.getAttribute('href');
    if (!href || !isCantinaUrl(href)) return;

    console.log('🔍 Chrome: Intercettato click su link cantina in Swiper', href);

    // In Chrome, forziamo la navigazione tramite Swup
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();

    // Aggiorna i metadati di navigazione
    updateNavMetaFromAnchor(link);

    // Avvia il cursor loading
    if (!isCursorLoading()) {
      startCursorLoading();
    }

    // Naviga dopo un piccolo delay per assicurarsi che tutto sia configurato
    setTimeout(() => {
      console.log('🔍 Chrome: Navigazione Swup avviata per', href);
      swup.navigate(href);
    }, 20);
  };

  // Usa capture per intercettare prima di Swiper
  document.addEventListener('click', window.chromeSwiperLinksHandler, { capture: true });
}

// Gestione navigazione per altri link interni (non menu, non cantine)
function handleOtherInternalLinks() {
  // Rimuovi eventuali listener precedenti
  if (window.otherLinksHandler) {
    document.removeEventListener('click', window.otherLinksHandler);
  }

  window.otherLinksHandler = (e) => {
    const link = e.target.closest('a');
    if (!link) return;

    // SKIP per selettori lingua WPML - devono fare navigazione normale (no transizioni custom)
    const isLanguageSwitcher = e.target.closest('.header-lang-switcher, .wpml-ls-item, .wpml-ls-link, .wpml-ls-item-legacy-list-horizontal, .wpml-ls-item-legacy-list-vertical, .wpml-ls-legacy-dropdown, .wpml-ls-menu-item, [class*="wpml-ls"]');
    // SKIP per selettori lingua WPML - navigazione gestita nativamente dal browser (escluso da Swup)
    if (e.target.closest('.header-lang-switcher, .wpml-ls-item, .wpml-ls-link, [class*="wpml-ls"]')) {
      return;
    }

    const href = link.getAttribute('href');

    // Skip per casi non necessari
    if (!href ||
      href === window.location.pathname ||
      href.startsWith('mailto:') ||
      href.startsWith('tel:') ||
      href.startsWith('#') ||
      (href.startsWith('http') && !href.includes(window.location.hostname))) {
      return;
    }

    // Skip per i link delle cantine SINGOLE - sono gestiti da handleCantineLinks()
    // Ma NON saltare l'archivio cantine (/cantine/ o /wineries/ senza slug dopo)
    if (isCantinaSingola(href) ||
      link.classList.contains('cantina-link-sw') ||
      link.classList.contains('agricole-card-btn')) {
      return;
    }

    // Skip per i link del menu - sono gestiti da initMenu()
    if (link.closest('#main-menu')) {
      return;
    }

    // Se è un link interno, gestisci la navigazione
    if (href.startsWith('/') || href.startsWith(window.location.origin) ||
      (!href.startsWith('http') && !href.includes('://'))) {

      e.preventDefault();

      // Imposta i parametri per la transizione default (solo colore, nessun logo)
      customOverlayParams = {
        logo: null,
        bg: '#e4dcd3',
        bgImage: null, // Nessuna immagine
        hideLogo: true
      };

      // Sincronizza con la variabile globale
      window.customOverlayParams = customOverlayParams;

      console.log('🔄 Transizione normale configurata (senza logo):', customOverlayParams);

      swup.navigate(href);
    }
  };

  document.addEventListener('click', window.otherLinksHandler);
}

// Gestione specifica per i click su #anchorSec nella sezione map-stage
function handleAnchorSecClick() {
  // Rimuovi eventuali listener precedenti
  if (window.anchorSecHandler) {
    document.removeEventListener('click', window.anchorSecHandler);
  }

  window.anchorSecHandler = (e) => {
    const link = e.target.closest('a[href]');
    if (!link) return;

    const href = link.getAttribute('href');

    // Intercetta solo i link che puntano a #anchorSec
    if (href !== '#anchorSec') return;

    // Disabilita il click su mobile - ignora i link con classe scroll-arrow-mob o se siamo su mobile
    const isMobile = window.innerWidth <= 1024 || 'ontouchstart' in window;
    const isMobileArrow = link.classList.contains('scroll-arrow-mob');

    if (isMobile || isMobileArrow) {
      // Su mobile, previeni il comportamento predefinito ma non fare scroll
      e.preventDefault();
      e.stopPropagation();
      return; // Esci senza fare scroll
    }

    // Previeni lo scroll predefinito del browser
    e.preventDefault();
    e.stopPropagation();

    // Trova l'elemento #anchorSec
    const anchorSec = document.getElementById('anchorSec');
    if (!anchorSec) {
      console.warn('⚠️ #anchorSec non trovato nel DOM');
      return;
    }

    // Trova la sezione map-stage che contiene #anchorSec
    const mapStage = anchorSec.closest('.map-stage');
    if (!mapStage) {
      console.warn('⚠️ .map-stage non trovato per #anchorSec');
      // Fallback: scroll normale con refresh ScrollTrigger
      anchorSec.scrollIntoView({ behavior: 'smooth', block: 'center' });
      // Aggiorna i ScrollTrigger dopo lo scroll
      setTimeout(() => {
        if (window.ScrollTrigger && ScrollTrigger.refresh) {
          ScrollTrigger.refresh();
        }
        if (window.gsap && gsap.ScrollTrigger && gsap.ScrollTrigger.refresh) {
          gsap.ScrollTrigger.refresh();
        }
      }, 300);
      return;
    }

    // Calcola la posizione corretta considerando che #anchorSec è position:absolute; bottom:70%
    // L'elemento è posizionato al 70% dal basso della sezione map-stage
    const mapStageRect = mapStage.getBoundingClientRect();
    const mapStageTop = mapStageRect.top + window.pageYOffset;
    const mapStageHeight = mapStageRect.height;

    // #anchorSec è al 70% dal basso, quindi è al 30% dall'alto della sezione
    // Calcola la posizione target per lo scroll - centra l'elemento nella viewport
    const targetPosition = mapStageTop + (mapStageHeight * 0.3);

    // Calcola l'offset per centrare l'elemento nella viewport
    const viewportHeight = window.innerHeight;
    const targetScrollY = targetPosition - (viewportHeight * 0.5);

    // Assicurati che lo scroll non vada oltre i limiti della pagina
    const finalScrollY = Math.max(0, Math.min(targetScrollY, document.documentElement.scrollHeight - viewportHeight));

    // Aggiorna i ScrollTrigger PRIMA dello scroll per evitare problemi di posizionamento
    if (window.ScrollTrigger && ScrollTrigger.refresh) {
      ScrollTrigger.refresh();
    }
    if (window.gsap && gsap.ScrollTrigger && gsap.ScrollTrigger.refresh) {
      gsap.ScrollTrigger.refresh();
    }

    // Esegui lo scroll controllato
    window.scrollTo({
      top: finalScrollY,
      behavior: 'smooth'
    });

    // Aggiorna i ScrollTrigger durante e dopo lo scroll per evitare problemi con .title-box
    // Monitora lo scroll e aggiorna i ScrollTrigger quando necessario
    let scrollUpdateCount = 0;
    const maxUpdates = 10; // Limita il numero di aggiornamenti
    const scrollUpdateInterval = setInterval(() => {
      scrollUpdateCount++;

      // Aggiorna i ScrollTrigger durante lo scroll
      if (window.ScrollTrigger && ScrollTrigger.refresh) {
        ScrollTrigger.refresh();
      }
      if (window.gsap && gsap.ScrollTrigger && gsap.ScrollTrigger.refresh) {
        gsap.ScrollTrigger.refresh();
      }

      // Verifica se lo scroll è completato
      const currentY = window.pageYOffset || window.scrollY;
      const scrollDiff = Math.abs(currentY - finalScrollY);

      // Se lo scroll è completato o abbiamo raggiunto il limite, ferma gli aggiornamenti
      if (scrollDiff < 5 || scrollUpdateCount >= maxUpdates) {
        clearInterval(scrollUpdateInterval);

        // Aggiornamento finale dopo che lo scroll è completato
        setTimeout(() => {
          if (window.ScrollTrigger && ScrollTrigger.refresh) {
            ScrollTrigger.refresh();
          }
          if (window.gsap && gsap.ScrollTrigger && gsap.ScrollTrigger.refresh) {
            gsap.ScrollTrigger.refresh();
          }
        }, 100);
      }
    }, 50); // Aggiorna ogni 50ms durante lo scroll
  };

  // Usa capture: true per intercettare prima degli altri handler
  document.addEventListener('click', window.anchorSecHandler, { capture: true });
}

// Initialize all functionality
function initAll() {
  // Aggiorna subito il theme-color per iOS Liquid Glass (prima del preloader)
  updateThemeColorForCantina('initAll');
  initPreloader();
  initMenu();
  initHamburgerMenu();
  initContactModal();
  initCursor();
  handleAnchorSecClick();
  handleUniversalCursorLoading();
  handleCantineLinks();
  handleChromeSwipersLinks(); // Fix specifico per Chrome
  handleOtherInternalLinks();
  initCantineSwiper();
  initGallerySwiper();
  initFocusModal();
  initArchiveCantine();
  initFooterMarquee();
  initGalleryScroll();
  gestisciFloatingLabels();
  initAllHomeParallaxWidgets();
  reinitializeContactForm7();
  initCustomSelect();
  initBackToTop();
  initGroupSwiper();
  initAllImageParallaxWidgets();
  initAllCardParallaxWidgets();

  // Gestisci il logo mirror al primo caricamento
  initCantinaHeroMirror();

  // Inizializza il colore dei loghi delle cantine
  initCantinaLogosColor();

  // Aggiorna il theme color per le barre del browser (iOS Liquid Glass)
  updateThemeColorForCantina('initAll-end');

  // Wait for pinsData to be available and render pins (vecchio sistema mappa SVG)
  setTimeout(() => {
    waitForPinsData();
  }, 3500);

  // Inizializza ScrollMapVideo (nuovo sistema con video)
  // Le funzioni sono già esposte su window.ScrollMap e lo shortcode PHP le chiamerà
  //console.log('📍 ScrollMapVideo funzioni esposte e pronte');

  // Inizializza SimpleScrollVideo per sezioni che lo richiedono
  // Safari ha bisogno di più tempo per caricare i video
  const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const videoInitDelay = isSafari ? 300 : 100;

  setTimeout(() => {
    initSimpleScrollVideoSections();
  }, videoInitDelay);
}

// Function to wait for pinsData to be available
function waitForPinsData(isSwupNavigation = false) {
  const pinsContainer = document.querySelector('.pins-container');
  if (!pinsContainer) {
    //console.log('📍 waitForPinsData - Nessun pins-container trovato');
    return;
  }

  // Se stiamo navigando con Swup, aspetta un po' di più per permettere al contenuto di caricarsi
  const initialDelay = isSwupNavigation ? 200 : 100;

  // Check if pinsData is already available
  if (typeof pinsData !== 'undefined' && Array.isArray(pinsData)) {
    renderCantinePins(pinsData, '.pins-container', isSwupNavigation);
    return;
  }

  // Prova a recuperare i dati dal DOM subito se disponibili
  if (isSwupNavigation) {
    const quickRecovery = tryRecoverPinsDataFromDOM(true);
    if (quickRecovery) return;
  }

  // If not available, wait and check again
  let attempts = 0;
  const maxAttempts = 100; // 10 seconds max (aumentato da 5)

  const checkPinsData = () => {
    attempts++;

    // Controlla sia la variabile globale che la presenza dello script nel DOM
    const pinsDataAvailable = typeof pinsData !== 'undefined' && Array.isArray(pinsData);
    const scriptExists = document.querySelector('.hero-wrapper script') ||
      Array.from(document.querySelectorAll('script')).some(script =>
        script.textContent && script.textContent.includes('pinsData'));

    if (pinsDataAvailable) {
      // Al primo caricamento, aspetta un po' di più per assicurarsi che il preloader sia completato
      // e che il video sia iniziato. Durante le navigazioni Swup, non serve questo delay
      const additionalDelay = (isFirstPageLoad && !isSwupNavigation) ? 500 : 0;

      setTimeout(() => {
        renderCantinePins(pinsData, '.pins-container', isSwupNavigation);
        // I pin saranno animati automaticamente dalla funzione renderCantinePins
        if (isFirstPageLoad && !isSwupNavigation) {
          isFirstPageLoad = false; // Marca che il primo caricamento è completato
        }
      }, additionalDelay);
    } else if (attempts < maxAttempts) {
      // Se lo script esiste ma la variabile non è ancora disponibile, aspetta meno
      const waitTime = scriptExists ? 50 : 100;
      setTimeout(checkPinsData, waitTime);
    } else {
      // Tentativo di recupero da DOM se presente
      tryRecoverPinsDataFromDOM();
    }
  };

  setTimeout(checkPinsData, initialDelay);
}

// // Funzione di recupero per estrarre pinsData dal DOM se la variabile globale non è disponibile
function tryRecoverPinsDataFromDOM(silent = false) {
  const scripts = Array.from(document.querySelectorAll('script'));

  //if (!silent) console.log('📍 tryRecoverPinsDataFromDOM - Cercando script con pinsData nel DOM');

  for (const script of scripts) {
    if (script.textContent && script.textContent.includes('pinsData')) {
      //if (!silent) console.log('📍 tryRecoverPinsDataFromDOM - Script con pinsData trovato:', script.textContent.substring(0, 200) + '...');
      try {
        // Estrai il contenuto JSON da pinsData
        const match = script.textContent.match(/const pinsData = (\[.*?\]);/s);
        if (match) {
          const jsonString = match[1];
          const parsedData = JSON.parse(jsonString);

          //if (!silent) console.log('📍 tryRecoverPinsDataFromDOM - Dati estratti:', parsedData);

          if (Array.isArray(parsedData)) {
            // Imposta la variabile globale e renderizza i pin
            window.pinsData = parsedData;
            renderCantinePins(parsedData, '.pins-container', true);
            //if (!silent) console.log('📍 Pins data recovered from DOM and rendered');
            return true;
          }
        }
      } catch (e) {
        if (!silent) console.warn('Error parsing pinsData from DOM:', e);
      }
    }
  }

  return false;
}

function renderCantinePins(pinsData, containerSelector = '.pins-container', isSwupNavigation = false) {
  const container = document.querySelector(containerSelector);
  if (!container || typeof pinsData === 'undefined' || !Array.isArray(pinsData)) return;

  // Pulisci eventuali pin esistenti
  container.innerHTML = '';

  // Evita di processare se non ci sono dati
  if (pinsData.length === 0) return;

  const video = document.querySelector('.scroll-video') || document.querySelector('.italy-video');
  const fps = 23.976;
  const intervalMs = 1000 / fps;
  const rewindSpeed = 1.5;
  const step = rewindSpeed * (intervalMs / 1000);

  // Funzione per ottenere il rewindTarget basato sull'ID del pin
  const getRewindTargetForPin = (pinId) => {

    // Mappatura specifica per ID (decommentare se preferisci)
    const rewindTargets = {
      'pin-1': 1.85,
      'pin-2': 2.3,
      'pin-3': 2,
      'pin-4': 1.90,
      'pin-5': 2.3,
      'pin-6': 2.2
    };
    return rewindTargets[pinId] || 1.85; // fallback al valore di default
  };

  pinsData.forEach(({ id, logo, x_desktop, y_desktop, x_tablet, y_tablet, x_mobile, y_mobile, link, title, rewind_target }, index) => {
    let rewindInterval = null;
    let hoverTimeout = null;
    let isHoverActive = false;
    let isMobileActive = false; // Stato per mobile: true se il logo è visibile

    // Ottieni il rewindTarget specifico per questo pin
    // Usa il valore dal PHP se presente, altrimenti calcola automaticamente
    const pinRewindTarget = rewind_target || getRewindTargetForPin(id, index);

    // Rilevamento dispositivo: desktop (>1024px), tablet (768-1024px), mobile (<768px)
    const width = window.innerWidth;
    const isMobile = width < 768 || 'ontouchstart' in window;
    const isTablet = width >= 768 && width <= 1024;

    // Posizioni responsive: sceglie coordinate appropriate per il dispositivo
    let x, y;
    if (isMobile) {
      // Mobile: usa mobile, fallback a tablet, poi desktop
      x = x_mobile ?? x_tablet ?? x_desktop;
      y = y_mobile ?? y_tablet ?? y_desktop;
    } else if (isTablet) {
      // Tablet: usa tablet, fallback a desktop
      x = x_tablet ?? x_desktop;
      y = y_tablet ?? y_desktop;
    } else {
      // Desktop: usa sempre desktop
      x = x_desktop;
      y = y_desktop;
    }

    // Dimensioni fisse - il CSS gestisce la scalatura su mobile con transform
    const pinWidth = 42;
    const pinHeight = 59;
    const expandedWidth = isMobile ? 150 : 170; // Solo la larghezza espansa può variare

    // Crea sempre un div per permettere la gestione custom del click
    const pin = document.createElement('div');
    pin.classList.add('pin');
    pin.id = id || `pin-${index + 1}`; // Usa l'ID dal PHP o fallback all'indice
    pin.style.setProperty('--x', `${x}%`);
    pin.style.setProperty('--y', `${y}%`);

    // Aggiungi attributi per accessibilità
    if (link) {
      pin.setAttribute('role', 'button');
      pin.setAttribute('tabindex', '0');
      pin.setAttribute('aria-label', title || 'Visita la cantina');
      pin.style.cursor = 'pointer';
    }

    pin.innerHTML = `
      <svg width="42" height="59" viewBox="0 0 42 59" fill="none" xmlns="http://www.w3.org/2000/svg" class="pin-svg">
        <g clip-path="url(#clip0_1_14264_${index})">
          <path d="M41.2031 20.572C41.2031 39.4844 20.683 58.4182 20.683 58.4182C20.683 58.4182 0.162964 39.4851 0.162964 20.572C0.162964 9.23793 9.3504 0.0493164 20.683 0.0493164C32.0157 0.0493164 41.2031 9.23793 41.2031 20.572Z" fill="#9D5024"/>
          <path d="M26.9207 19.1276L29.8712 12.3693L22.486 14.9146L17.6387 9.16943L16.323 16.3526L8.37097 16.8226L13.4564 21.6624C13.4564 21.6624 13.4421 21.6383 11.8035 29.3271L19.0277 26.232L23.8797 31.9695L25.1964 24.7786L32.995 23.7655L26.9198 19.1267L26.9207 19.1276Z" fill="#E5DBD3"/>
        </g>
        <defs>
          <clipPath id="clip0_1_14264_${index}">
            <rect width="41.0401" height="58.3682" fill="white" transform="translate(0.162964 0.0493164)"/>
          </clipPath>
        </defs>
      </svg>
      <svg xmlns="http://www.w3.org/2000/svg" class="pin-arrow" width="45" height="46" fill="none" style="display: none;">
        <circle cx="22.206" cy="22.206" r="22.206" fill="#9D5024" transform="matrix(1 0 0 -1 .418 45.216)"/>
        <circle cx="22.206" cy="22.206" r="22.206" fill="#9D5024" transform="matrix(1 0 0 -1 .418 45.216)"/>
        <g clip-path="url(#a)">
          <path fill="#BCA281" stroke="#BCA281" stroke-width=".25" d="m29.66 16.145.017.144.101.567.089.372c.107.41.27.92.522 1.45.823 1.732 2.137 2.845 3.912 3.313l.32.085-.153.075v1.19l-.09.026-.08.023-.001-.001c-1.766.51-3.077 1.74-3.907 3.659h.001c-.252.59-.419 1.16-.527 1.614l-.09.413c-.052.276-.083.499-.1.635-.01.083-.017.127-.017.157v.17h-.12l-.01.11h-.115l-.089-.005-.357-.022a2.428 2.428 0 0 1-.431-.054.462.462 0 0 1-.139-.055.182.182 0 0 1-.088-.153v-.094l.023-.211v-.001a11.365 11.365 0 0 1 .82-3.035c.56-1.304 1.334-2.358 2.303-3.146H9.644v-1.357h21.653c-.893-.704-1.614-1.627-2.144-2.751a9.64 9.64 0 0 1-.827-2.794v-.001a2.443 2.443 0 0 0-.024-.18l-.003-.013v-.096c0-.08.052-.129.09-.154a.483.483 0 0 1 .14-.056c.102-.027.248-.044.43-.056l.375-.028.06-.003h.01v-.001l.123-.009.134-.009v.282Z"/>
        </g>
        <defs>
          <clipPath id="a">
            <path fill="#fff" d="M9.768 15.997h24.58v14.025H9.768z"/>
          </clipPath>
        </defs>
      </svg>
      ${logo ? `<img src="${logo}" alt="${title || 'Cantina logo'}" class="cantina-logo">` : ''}
    `;

    // NON cambiare mai width/height per evitare movimento
    // Il pin rimane sempre 42x59 per mantenere il transform stabile
    gsap.set(pin, { opacity: 0 });

    // Imposta lo stato iniziale del logo
    const img = pin.querySelector('.cantina-logo');
    if (img) {
      gsap.set(img, {
        display: 'none',
        opacity: 0,
        y: 20
      });
    }

    // Gestione click per mobile (doppio tap) e desktop
    pin.addEventListener('click', (e) => {
      e.preventDefault(); // Previene il comportamento di default

      if (!link) return; // Se non c'è link, non fare nulla

      if (isMobile) {
        // Logica mobile: primo tap mostra logo, secondo tap naviga
        if (!isMobileActive) {
          // Primo tap: mostra il logo E fa rewind del video (come mouseenter)
          isMobileActive = true;

          const cursor = document.getElementById('magic-cursor');
          const svg = pin.querySelector('.pin-svg');
          const arrow = pin.querySelector('.pin-arrow');
          const img = pin.querySelector('.cantina-logo');

          // Animazioni pin (come desktop)
          gsap.to(svg, { opacity: 0, duration: 0.3 });
          gsap.to(arrow, { display: 'block', opacity: 1, duration: 0.3 });

          if (img) {
            gsap.set(img, {
              display: 'block',
              opacity: 0,
              xPercent: 0,
              y: 20,
              transformOrigin: 'bottom center'
            });
            gsap.to(img, {
              opacity: 1,
              y: 0,
              duration: 0.3,
              ease: 'power2.out'
            });
          }
          // Rimosso gsap.to(pin, { width: expandedWidth, height: 'auto' }) per evitare movimento

          if (cursor) gsap.set(cursor, { display: 'none' });

          // MOBILE: Pausa video e rewind (come mouseenter desktop)
          clearInterval(rewindInterval);
          if (video) {
            video.pause();
            rewindInterval = setInterval(() => {
              if (video.currentTime > pinRewindTarget) {
                video.currentTime = Math.max(video.currentTime - step, pinRewindTarget);
              } else {
                clearInterval(rewindInterval);
              }
            }, intervalMs);
          }

          // Nascondi tutti gli altri pin attivi E riavvia il video
          document.querySelectorAll('.pin').forEach(otherPin => {
            if (otherPin !== pin && otherPin.classList.contains('mobile-active')) {
              const otherSvg = otherPin.querySelector('.pin-svg');
              const otherArrow = otherPin.querySelector('.pin-arrow');
              const otherImg = otherPin.querySelector('.cantina-logo');

              gsap.to(otherSvg, { opacity: 1, duration: 0.3 });
              gsap.to(otherArrow, {
                opacity: 0,
                duration: 0.3,
                onComplete: () => otherArrow.style.display = 'none'
              });

              if (otherImg) {
                gsap.to(otherImg, {
                  opacity: 0,
                  y: 20,
                  duration: 0.3,
                  ease: 'power2.in',
                  onComplete: () => otherImg.style.display = 'none'
                });
              }
              // Rimosso gsap.to(otherPin, { width: 42, height: 59 }) per mantenere dimensioni costanti
              otherPin.classList.remove('mobile-active');

              // Riavvia il video quando si chiude un altro pin
              if (video) {
                playVideoSafely();
              }
            }
          });

          pin.classList.add('mobile-active');
        } else {
          // Secondo tap: ferma rewind e naviga
          clearInterval(rewindInterval);

          customOverlayParams = {
            logo: logo,
            bg: '#6f9c76',
            hideLogo: false
          };

          if (!isCursorLoading()) {
            startCursorLoading();
          }

          swup.navigate(link);
        }
      } else {
        // Desktop: naviga immediatamente
        customOverlayParams = {
          logo: logo,
          bg: '#6f9c76',
          hideLogo: false
        };

        if (!isCursorLoading()) {
          startCursorLoading();
        }

        swup.navigate(link);
      }
    });

    // Chiudi il pin mobile se si clicca altrove E riavvia video (come mouseleave)
    if (isMobile) {
      document.addEventListener('click', (e) => {
        if (!pin.contains(e.target) && isMobileActive) {
          isMobileActive = false;

          // MOBILE: Ferma il rewind e riavvia il video (come mouseleave desktop)
          clearInterval(rewindInterval);

          const cursor = document.getElementById('magic-cursor');
          const svg = pin.querySelector('.pin-svg');
          const arrow = pin.querySelector('.pin-arrow');
          const img = pin.querySelector('.cantina-logo');

          gsap.to(svg, { opacity: 1, duration: 0.3 });
          gsap.to(arrow, {
            opacity: 0,
            duration: 0.3,
            onComplete: () => arrow.style.display = 'none'
          });

          if (img) {
            gsap.to(img, {
              opacity: 0,
              y: 20,
              duration: 0.3,
              ease: 'power2.in',
              onComplete: () => img.style.display = 'none'
            });
          }
          // Rimosso gsap.to(pin, { width: 42, height: 59 }) per mantenere dimensioni costanti

          if (cursor) gsap.set(cursor, { display: 'block' });
          pin.classList.remove('mobile-active');

          // MOBILE: Riavvia il video (come mouseleave desktop)  
          if (video) {
            playVideoSafely();
          }
        }
      });
    }

    // Event listeners per desktop (mouseenter/mouseleave) - solo se non mobile
    if (!isMobile) {
      pin.addEventListener('mouseenter', (e) => {
        // ritardo 150ms: se esci prima, non succede nulla
        hoverTimeout = setTimeout(() => {
          isHoverActive = true;

          // animazioni pin
          const cursor = document.getElementById('magic-cursor');
          const svg = pin.querySelector('.pin-svg');
          const arrow = pin.querySelector('.pin-arrow');
          const img = pin.querySelector('.cantina-logo');
          gsap.to(svg, { opacity: 0, duration: 0.3 });
          gsap.to(arrow, { display: 'block', opacity: 1, duration: 0.3 });

          if (img) {
            gsap.set(img, {
              display: 'block',
              opacity: 0,
              xPercent: 0,
              y: 20,
              transformOrigin: 'bottom center'
            });
            gsap.to(img, {
              opacity: 1,
              y: 0,
              duration: 0.3,
              ease: 'power2.out'
            });
          }
          // Rimosso gsap.to(pin, { width: expandedWidth, height: 'auto' }) per evitare movimento
          gsap.set(cursor, { display: 'none' });

          // Nascondi tutti gli altri pin
          document.querySelectorAll('.pin').forEach(otherPin => {
            if (otherPin !== pin) {
              gsap.to(otherPin, { opacity: 0, duration: 0.3 });
            }
          });

          // rewind
          clearInterval(rewindInterval);
          if (video) {
            video.pause();
            rewindInterval = setInterval(() => {
              if (video.currentTime > pinRewindTarget) {
                video.currentTime = Math.max(video.currentTime - step, pinRewindTarget);
              } else {
                clearInterval(rewindInterval);
              }
            }, intervalMs);
          }
        }, 150);
      });

      pin.addEventListener('mouseleave', () => {
        const cursor = document.getElementById('magic-cursor');
        gsap.set(cursor, { display: 'block' });
        // se siamo usciti prima del timeout, cancello
        clearTimeout(hoverTimeout);

        if (!isHoverActive) {
          // non era un hover "vero"
          return;
        }
        // altrimenti è un vero mouseleave dopo hover
        isHoverActive = false;
        clearInterval(rewindInterval);

        const svg = pin.querySelector('.pin-svg');
        const arrow = pin.querySelector('.pin-arrow');
        const img = pin.querySelector('.cantina-logo');
        gsap.to(svg, { opacity: 1, duration: 0.3 });
        gsap.to(arrow, {
          opacity: 0,
          duration: 0.3,
          onComplete: () => arrow.style.display = 'none'
        });

        if (img) {
          gsap.to(img, {
            opacity: 0,
            y: 20,
            duration: 0.3,
            ease: 'power2.in',
            onComplete: () => img.style.display = 'none'
          });
        }
        gsap.to(pin, { width: 42, height: 59, duration: 0.3 });

        // Riporta tutti gli altri pin alla loro opacità normale
        document.querySelectorAll('.pin').forEach(otherPin => {
          if (otherPin !== pin) {
            gsap.to(otherPin, { opacity: 1, duration: 0.3 });
          }
        });

        if (video) {
          playVideoSafely();
        }
      });
    } // chiusura del blocco if (!isMobile)

    container.append(pin);
  });

  // Anima i pin con delay appropriato (4 secondi per primo caricamento, 0.5 secondi per navigazioni Swup)
  const delay = isSwupNavigation ? 0.5 : 4;
  animatePinsWithDelay(delay);
}

// Menu
function initMenu() {
  const content = document.querySelector('#swup');
  const menuContainer = document.querySelector('#site-header .container');
  const menuItems = document.querySelectorAll('#main-menu li');
  const currentItem = document.querySelector('#main-menu li.current-menu-item');
  const menuLinks = document.querySelectorAll('#main-menu a');

  // Aggiunge/rimuove la classe 'is-scrolling' a menuContainer quando si scrolla oltre 50px dal top
  function handleMenuScroll() {
    if (!menuContainer) return;
    if (window.scrollY > 50) {
      menuContainer.classList.add('is-scrolling');
    } else {
      menuContainer.classList.remove('is-scrolling');
    }
  }
  window.addEventListener('scroll', handleMenuScroll);
  // Esegui subito per lo stato iniziale
  handleMenuScroll();

  menuItems.forEach(item => {
    item.addEventListener('mouseenter', () => {
      if (currentItem) {
        currentItem.classList.add('hide-underline');
      }
    });

    item.addEventListener('mouseleave', () => {
      if (currentItem) {
        currentItem.classList.remove('hide-underline');
      }
    });
  });

  // Gestione clic menu con overlay (l'animazione cursor è gestita universalmente)
  menuLinks.forEach(link => {
    link.addEventListener('click', (e) => {
      const href = link.getAttribute('href');

      // Skip se è il link della pagina corrente o se è un link esterno
      if (!href || href === window.location.pathname || href.startsWith('http') || href.startsWith('mailto') || href.startsWith('tel')) {
        return;
      }

      e.preventDefault();

      // Avvia l'animazione del cursor anche per i link del menu
      if (!isCursorLoading()) {
        startCursorLoading();
      }

      // Verifica se il link del menu porta a una cantina SINGOLA
      // L'archivio (/cantine/ o /wineries/) non deve avere il logo
      if (isCantinaSingola(href)) {
        // Se è un link a una cantina SINGOLA dal menu, cerca i dati della cantina
        if (window.cantineData && Array.isArray(window.cantineData)) {
          const targetCantina = window.cantineData.find(c =>
            href.includes(c.url) || href.includes(c.slug) ||
            href.includes('/cantine/' + c.slug) || href.includes('/wineries/' + c.slug)
          );

          if (targetCantina) {
            customOverlayParams = {
              logo: targetCantina.logo,
              bg: targetCantina.colore,
              bgImage: targetCantina.hero_bg, // Immagine hero della cantina
              hideLogo: false
            };
            window.customOverlayParams = customOverlayParams;
            console.log('🔄 Menu: transizione cantina configurata (con logo e immagine)');
          }
        }
      } else {
        // Se NON è un link a una cantina singola (incluso archivio), pulisci i parametri (senza logo)
        customOverlayParams = {
          logo: null,
          bg: '#e4dcd3',
          bgImage: null, // Nessuna immagine
          hideLogo: true
        };
        window.customOverlayParams = customOverlayParams;
        console.log('🔄 Menu: transizione normale configurata (senza logo)');
      }

      // Preparazione overlay per menu
      const overlay = ensureTransitionOverlay(null, null, false, null);
      overlay.style.display = 'block';
      gsap.set(overlay, { opacity: 0 });

      gsap.to(overlay, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.inOut',
        onStart: () => overlay.style.pointerEvents = 'auto',
        onComplete: () => {
          // Naviga con Swup
          swup.navigate(href);
        }
      });
    });
  });


}

// Gestione scroll restoration (come nel codice di riferimento)
history.scrollRestoration = 'manual';

// Funzione helper per forzare scroll in alto in modo aggressivo
function forceScrollToTop(immediate = true) {
  // FORZA scroll behavior a 'auto' PRIMA di qualsiasi scroll
  const currentBehavior = document.documentElement.style.scrollBehavior;
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.scrollBehavior = 'auto';

  // Metodo 1: window.scrollTo immediato
  window.scrollTo(0, 0);

  // Metodo 2: documentElement
  document.documentElement.scrollTop = 0;

  // Metodo 3: body
  document.body.scrollTop = 0;

  // Metodo 4: window.scroll
  window.scroll(0, 0);

  // Metodo 5: scrollTo con options immediate
  window.scrollTo({ top: 0, left: 0, behavior: 'auto' });

  // Se immediate, mantieni 'auto' per un breve periodo prima di ripristinare
  if (immediate) {
    // Mantieni 'auto' per 100ms per assicurarsi che non ci siano scroll smooth residui
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = currentBehavior;
      document.body.style.scrollBehavior = '';
    }, 100);
  } else {
    setTimeout(() => {
      document.documentElement.style.scrollBehavior = currentBehavior;
      document.body.style.scrollBehavior = '';
    }, 50);
  }
}

// Esponi la funzione globalmente
window.forceScrollToTop = forceScrollToTop;

document.addEventListener('DOMContentLoaded', () => {
  history.scrollRestoration = 'manual';
  // Forza scroll in alto al caricamento iniziale
  forceScrollToTop();
});

window.addEventListener('load', () => {
  history.scrollRestoration = 'manual';
  // Forza scroll in alto dopo il load (solo al primo caricamento)
  if (isFirstPageLoad) {
    setTimeout(() => forceScrollToTop(), 50);
  }
});

// Note: pageshow rimosso - Swup gestisce lo scroll per back/forward
// Conflitti con pageshow causavano problemi con la navigazione del browser

// Initialize back to top button functionality
function initBackToTop() {
  const backTopButton = document.getElementById('backTop');
  if (backTopButton) {
    backTopButton.addEventListener('click', function (e) {
      e.preventDefault();

      // Smooth scroll to top with custom animation
      function smoothScrollToTop() {
        const startPosition = window.pageYOffset;
        const startTime = performance.now();
        const duration = 800; // 800ms per un scroll più fluido

        function scrollStep(currentTime) {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          // Easing function per un movimento più naturale (ease-out)
          const easeOutCubic = 1 - Math.pow(1 - progress, 3);

          const currentPosition = startPosition * (1 - easeOutCubic);
          window.scrollTo(0, currentPosition);

          if (progress < 1) {
            requestAnimationFrame(scrollStep);
          }
        }

        requestAnimationFrame(scrollStep);
      }

      smoothScrollToTop();
    });
  }
}

// Initialize on page load
document.addEventListener('DOMContentLoaded', initAll);

// Funzione helper per inizializzare ScrollMapVideo dai data-attributes - DISABILITATA
function reinitScrollMapVideo() {
  // DISABILITATO ScrollMapVideo - conflitti con SimpleScrollVideo
  //console.log('📍 reinitScrollMapVideo DISABILITATA - usa SimpleScrollVideo');
  return;
}

// RIMOSSO: listener popstate - Swup gestisce già back/forward del browser tramite i suoi hook
// Non serve un listener popstate separato perché creerebbe conflitti con Swup

// Handle window resize for responsive pin positions
let resizeTimeout;
window.addEventListener('resize', () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    const pinsContainer = document.querySelector('.pins-container');
    if (pinsContainer && typeof pinsData !== 'undefined' && Array.isArray(pinsData)) {
      console.log('🔄 Aggiornamento posizioni pin per resize...');
      // Ri-renderizza i pin con le nuove posizioni responsive
      renderCantinePins(pinsData, '.pins-container', true);
    }
  }, 250); // Debounce di 250ms per evitare troppe chiamate
});

// Observer per rilevare quando la mappa viene aggiunta dinamicamente
const observer = new MutationObserver((mutations) => {
  mutations.forEach((mutation) => {
    mutation.addedNodes.forEach((node) => {
      if (node.nodeType === 1) { // Element node
        const pinsContainer = node.querySelector('.pins-container');
        if (pinsContainer) {
          // Aggiungi un breve ritardo per assicurarsi che il DOM sia pronto
          setTimeout(() => {
            // Usa waitForPinsData() invece di controllare immediatamente
            waitForPinsData(true); // Indica che è una navigazione dinamica
          }, 100);
        }
      }
    });
  });
});

// Inizia a osservare il body per cambiamenti
observer.observe(document.body, {
  childList: true,
  subtree: true
});


// Gestione Safari specifica per wheel (come nel codice di riferimento)
function isSafari() {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

if (isSafari()) {
  window.dispatchEvent(new CustomEvent('scroll/defaultWheel'));
}

// Funzione vhSafari per gestione viewport height (come nel codice di riferimento)
function vhSafari() {
  if (!isSafari()) return;

  const fnSize = () => {
    document.body.style.setProperty('--vh', `${window.innerHeight}px`);
  };

  let currW = window.innerWidth;
  let prevW = currW;

  window.addEventListener('resize', () => {
    currW = window.innerWidth;

    if (matchMedia('(hover:none) and (pointer:coarse)').matches) {
      if (prevW !== currW) fnSize();
    } else {
      fnSize();
    }

    prevW = currW;
  });

  fnSize();
}

// Inizializza vhSafari
vhSafari();

// Inizializza scroll smooth 
initSmoothScroll();

// Mantieni la compatibilità con le chiamate esistenti
window.lenis = {
  scrollTo: scrollTo,
  stop: disableScroll,
  start: enableScroll
};

// Expose cursor functions globally for hamburger menu
window.startCursorLoading = startCursorLoading;
window.stopCursorLoading = stopCursorLoading;
window.isCursorLoading = isCursorLoading;

// Expose customOverlayParams globally for map pins
window.customOverlayParams = customOverlayParams;

// Expose SimpleScrollVideo functions globally
if (typeof window.ScrollMap === 'undefined') {
  window.ScrollMap = {};
}
window.ScrollMap.initSimpleScrollVideo = initSimpleScrollVideo;
window.ScrollMap.registerScrollMapPins = registerScrollMapPins;
window.ScrollMap.renderPins = renderPins;

// Tiene traccia delle istanze di ScrollMapVideo per poterle distruggere
window.ScrollMapInstances = window.ScrollMapInstances || [];

// Tiene traccia delle istanze di SimpleScrollVideo per poterle distruggere
window.SimpleScrollVideoInstances = window.SimpleScrollVideoInstances || [];

// Expose scroll functions globally for compatibility
window.scrollTo = scrollTo;
window.disableScroll = disableScroll;
window.enableScroll = enableScroll;

// Expose transition state globally
Object.defineProperty(window, 'isTransitioning', {
  get: () => isTransitioning,
  set: (value) => { isTransitioning = value; }
});

// Expose reinit function globally for debugging (DISABILITATA)
window.reinitScrollMapVideo = reinitScrollMapVideo;

// Expose debug functions globally for parallax widgets
window.debugAllParallaxWidgets = debugAllParallaxWidgets;
window.debugHomeParallaxWidgets = () => debugParallaxWidgetsByType('home-parallax');
window.debugImageParallaxWidgets = () => debugParallaxWidgetsByType('image-parallax');
window.debugCardParallaxWidgets = () => debugParallaxWidgetsByType('card-parallax');

// Funzione per inizializzare SimpleScrollVideo
function initSimpleScrollVideoSections() {
  //console.log('📍 Inizializzazione SimpleScrollVideo...');

  // Distruggi le istanze precedenti
  if (window.SimpleScrollVideoInstances && window.SimpleScrollVideoInstances.length > 0) {
    console.log('📍 Pulizia istanze SimpleScrollVideo precedenti');
    window.SimpleScrollVideoInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    window.SimpleScrollVideoInstances = [];
  }

  // Trova tutte le sezioni con data-scrollmap che usano il nuovo sistema
  const scrollMapSections = document.querySelectorAll('[data-scrollmap]');

  if (scrollMapSections.length === 0) {
    console.log('📍 Nessuna sezione ScrollMap trovata');
    return;
  }

  let initialized = 0;

  scrollMapSections.forEach((section, index) => {
    const sectionId = section.getAttribute('data-scrollmap');

    try {
      // Controlla se questa sezione deve usare il nuovo sistema SimpleScrollVideo
      const useSimpleVideo = section.hasAttribute('data-use-simple-video') &&
        section.getAttribute('data-use-simple-video') === 'true';

      if (!useSimpleVideo) {
        console.log(`📍 Sezione ${sectionId}: usa il sistema ScrollMapVideo tradizionale`);
        return;
      }

      //console.log(`📍 [${index + 1}/${scrollMapSections.length}] Inizializzazione SimpleScrollVideo per sezione: ${sectionId}`);

      // Leggi i data-attributes
      const videoId = section.getAttribute('data-video-id');
      const pinsId = section.getAttribute('data-pins-id');

      if (!videoId || !pinsId) {
        console.warn(`📍 ⚠️ Sezione ${sectionId}: mancano data-video-id o data-pins-id`);
        return;
      }

      // Trova gli elementi
      const videoEl = document.getElementById(videoId);
      const pinsEl = document.getElementById(pinsId);

      if (!videoEl) {
        console.warn(`📍 ⚠️ Sezione ${sectionId}: video element #${videoId} non trovato`);
        return;
      }

      if (!pinsEl) {
        console.warn(`📍 ⚠️ Sezione ${sectionId}: pins container #${pinsId} non trovato`);
        return;
      }

      // Configurazione per SimpleScrollVideo
      const webmSrcAttr = section.getAttribute('data-video-webm-src');
      const webmSrc = webmSrcAttr && webmSrcAttr.trim() !== '' ? webmSrcAttr : null;

      // Leggi parametri per browser-specific (solo se non vuoti)
      const rewindSpeedAttr = section.getAttribute('data-rewind-speed');
      const smoothnessAttr = section.getAttribute('data-smoothness');
      const returnSmoothnessAttr = section.getAttribute('data-return-smoothness');
      const firstPlaybackSmoothnessAttr = section.getAttribute('data-first-playback-smoothness');

      const config = {
        scrollTrigger: parseInt(section.getAttribute('data-scroll-trigger')) || 200,
        videoDuration: parseFloat(section.getAttribute('data-video-duration')) || 1.47,
        fps: parseInt(section.getAttribute('data-fps')) || 30,
        webmSrc: webmSrc,
        debug: section.getAttribute('data-debug') === 'true'
      };

      // Aggiungi solo i parametri browser-specific se non sono vuoti
      // Questo permette al browserSettings in simpleScrollVideo.js di avere la priorità
      if (rewindSpeedAttr && rewindSpeedAttr.trim() !== '') {
        config.rewindSpeed = parseFloat(rewindSpeedAttr);
      }
      if (smoothnessAttr && smoothnessAttr.trim() !== '') {
        config.smoothness = parseFloat(smoothnessAttr);
      }
      if (returnSmoothnessAttr && returnSmoothnessAttr.trim() !== '') {
        config.returnSmoothness = parseFloat(returnSmoothnessAttr);
      }
      if (firstPlaybackSmoothnessAttr && firstPlaybackSmoothnessAttr.trim() !== '') {
        config.firstPlaybackSmoothness = parseFloat(firstPlaybackSmoothnessAttr);
      }

      //console.log(`📍 Config SimpleScrollVideo per sezione ${sectionId}:`, config);

      // if (webmSrc) {
      //   console.log(`🎬 WebM URL configurato: ${webmSrc}`);
      // } else {
      //   console.log(`⚠️ WebM URL non specificato - verrà usato il video MP4 originale`);
      // }

      // Inizializza SimpleScrollVideo
      const instance = initSimpleScrollVideo(videoEl, config);

      if (instance) {
        // Salva l'istanza
        window.SimpleScrollVideoInstances.push(instance);

        // Leggi e crea i pin nel DOM
        const pinsDataStr = section.getAttribute('data-pins-data');
        let pinsData = [];
        if (pinsDataStr) {
          try {
            pinsData = JSON.parse(pinsDataStr);
          } catch (e) {
            console.warn(`📍 ⚠️ Errore parsing pinsData per sezione ${sectionId}:`, e);
          }
        }

        if (pinsData.length > 0) {
          // Crea i pin nel DOM (passando l'istanza del video per salvare il riferimento al pinsContainer)
          renderPins(pinsData, pinsEl, section, instance);

          // Registra i pin con l'istanza SimpleScrollVideo
          registerScrollMapPins(instance, pinsEl);

          // Aggiungi listener per aggiornare le posizioni dei pin al resize
          let resizeTimeout;
          const resizeHandler = () => {
            clearTimeout(resizeTimeout);
            resizeTimeout = setTimeout(() => {
              if (typeof updatePinPositions === 'function') {
                updatePinPositions(pinsEl);
              }
            }, 250); // Debounce di 250ms
          };
          window.addEventListener('resize', resizeHandler);

          // Salva il riferimento al handler per poterlo rimuovere se necessario
          if (!window._scrollMapResizeHandlers) {
            window._scrollMapResizeHandlers = [];
          }
          window._scrollMapResizeHandlers.push({ section: sectionId, handler: resizeHandler, container: pinsEl });
        } else {
          console.warn(`📍 ⚠️ Nessun pin data trovato per sezione ${sectionId}`);
        }

        //console.log(`📍 ✅ SimpleScrollVideo inizializzato per sezione ${sectionId}`);
        initialized++;
      } else {
        console.warn(`📍 ⚠️ Fallimento inizializzazione SimpleScrollVideo per sezione ${sectionId}`);
      }

    } catch (e) {
      console.error(`📍 ❌ Errore durante inizializzazione SimpleScrollVideo sezione ${sectionId}:`, e);
    }
  });

  // if (initialized === 0) {
  //   console.log('📍 ⚠️ Nessuna sezione SimpleScrollVideo trovata o inizializzata');
  // } else {
  //   console.log(`📍 ✅ Inizializzazione SimpleScrollVideo completata: ${initialized}/${scrollMapSections.length} sezioni`);
  // }
}

// Expose la funzione globalmente
window.initSimpleScrollVideoSections = initSimpleScrollVideoSections;

function getHeroLogoFinalDelay(baseDelaySec) {
  const EXTRA_TEST_DELAY = 0.5; // Ridotto da 1.0 a 0.5 per velocizzare
  // override rapido da HTML o da localStorage (comodo per test)
  const ds = parseFloat(document.body.dataset.heroLogoDelay || '0');           // es: <body data-hero-logo-delay="1.5">
  const ls = parseFloat(localStorage.getItem('heroLogoDelay') || '0');         // es: localStorage.setItem('heroLogoDelay','2')
  return baseDelaySec + EXTRA_TEST_DELAY + (isNaN(ds) ? 0 : ds) + (isNaN(ls) ? 0 : ls);
}


// Funzione helper per gestire la visibilità del logo della cantina
function handleCantinaLogoVisibility(isTransitioningFromCantina, animated = false) {
  const mainCantinaLogo = document.querySelector('.cantina-hero .cantina-logo');
  if (!mainCantinaLogo) return;

  gsap.killTweensOf(mainCantinaLogo);

  if (isTransitioningFromCantina) {
    // Nascondo ma senza rimuovere dal layout
    gsap.set(mainCantinaLogo, { autoAlpha: 0, clearProps: 'x,y,scale' });
    mainCantinaLogo.dataset.deferShow = '1'; // flag per debug/controlli se serve
    console.log('🏷️ Logo cantina nascosto (transizione cantina→cantina)');
  } else {
    delete mainCantinaLogo.dataset.deferShow;

    if (animated) {
      gsap.set(mainCantinaLogo, { autoAlpha: 0, y: 20, clearProps: 'x,scale' });
      gsap.to(mainCantinaLogo, {
        autoAlpha: 1,
        y: 0,
        duration: 0.6, // Ridotto da 0.8 a 0.6
        ease: 'power2.out',
      });
      console.log('🏷️ Logo cantina animato (navigazione normale)');
    } else {
      gsap.set(mainCantinaLogo, { autoAlpha: 1, clearProps: 'x,y,scale' });
      console.log('🏷️ Logo cantina mostrato (navigazione normale)');
    }
  }
}

// Funzione helper per gestire la visibilità del logo mirror al primo caricamento
function initCantinaHeroMirror() {
  const heroMirror = document.querySelector('.cantina-hero-mirror');

  if (heroMirror && document.querySelector('main.cantina-single')) {
    // Al primo caricamento di una pagina cantina, mostra il mirror
    gsap.set(heroMirror, { autoAlpha: 1 });

    // Non nascondiamo più alcun logo - tutti i loghi in cant-logos devono rimanere visibili
    // Il preview-logo è commentato nel footer, quindi non c'è bisogno di gestire la sua visibilità
    //console.log('🏷️ Logo mirror mostrato al primo caricamento, tutti i loghi rimangono visibili');
  }
}

// Funzione per inizializzare il colore dei loghi delle cantine
function initCantinaLogosColor() {
  const cantLogos = document.querySelectorAll('.cant-logos .cantina-logo');
  if (cantLogos.length === 0) return;

  // Applica il colore speciale al logo della cantina attuale (classe .active)
  const activeLogo = document.querySelector('.cant-logos .cantina-logo.active');
  if (activeLogo) {
    const accentColor = 'rgba(255, 255, 255, 1)';

    // Applica il colore ai path SVG
    const svgPaths = activeLogo.querySelectorAll('svg path');
    svgPaths.forEach(path => {
      gsap.set(path, {
        fill: accentColor
      });
    });
    //console.log('🏷️ Logo cantina attuale colorato:', activeLogo);
  }

  // Assicurati che tutti gli altri loghi siano nel loro stato normale
  cantLogos.forEach(logo => {
    if (!logo.classList.contains('active')) {
      const originalColor = 'rgba(255, 255, 255, 0.2)'; // Colore originale degli SVG

      const svgPaths = logo.querySelectorAll('svg path');
      svgPaths.forEach(path => {
        gsap.set(path, {
          fill: originalColor
        });
      });
    }
  });
}
window.handleCantinaLogoVisibility = handleCantinaLogoVisibility;

// Funzione globale per aggiornare i metadati di navigazione
function updateNavMetaFromAnchor(a) {
  const href = a.getAttribute('href') || '';
  const url = new URL(href, location.origin);
  const isInternal = url.origin === location.origin;

  // SKIP per selettori lingua WPML - devono fare transizioni normali, non cantina-to-cantina
  const isLanguageSwitcher = !!a.closest('.header-lang-switcher, .wpml-ls-item, .wpml-ls-link, .wpml-ls-item-legacy-list-horizontal, .wpml-ls-item-legacy-list-vertical, .wpml-ls-legacy-dropdown, .wpml-ls-menu-item, [class*="wpml-ls"]');

  // Distingui tra archivio cantine (/cantine/ o /wineries/) e cantina singola
  const isArchive = isCantinaArchive(url.pathname);
  const isCantina = isCantinaSingola(url.pathname); // Solo cantine singole

  const insideSwiper = !!a.closest('.swiper, .swiper-container, .swiper-slide');
  const noTransition = a.hasAttribute('data-no-cantina-transition') || a.classList.contains('no-cantina-transition');

  // Per i link delle cantine dentro lo Swiper, permettiamo comunque le transizioni cantina
  // ma le trattiamo come navigazione normale (senza animazioni speciali di scroll)
  const isCantinaSwiper = insideSwiper && a.closest('.cantine-swiper-container, .cantine-swiper');

  // IMPORTANTE: Se è un selettore lingua WPML, NON permettere transizioni cantina
  // Questo evita il loop infinito quando si cambia lingua da una pagina cantina
  const allowCantina = isInternal && isCantina && (!insideSwiper || isCantinaSwiper) && !noTransition && !isLanguageSwitcher;

  window._swupNav = {
    href: url.href,
    insideSwiper,
    isCantina, // Solo true per cantine singole, false per archivio
    isCantinaArchive: isArchive, // Nuovo: true solo per archivio
    allowCantina,
    isCantinaSwiper: !!isCantinaSwiper,
    isLanguageSwitcher // Nuovo: traccia se è un cambio lingua
  };

  if (isLanguageSwitcher) {
    console.log('🌐 _swupNav: Rilevato selettore lingua WPML - transizione cantina disabilitata');
  } else {
    console.log('🔄 _swupNav aggiornato:', window._swupNav);
  }
}

// Esponi la funzione globalmente
window.updateNavMetaFromAnchor = updateNavMetaFromAnchor;

// 1) Installo il click tracker (una sola volta)
if (!window._swupClickTrackerInstalled) {
  window._swupClickTrackerInstalled = true;

  window._swupNav = { href: null, insideSwiper: false, isCantina: false, allowCantina: false, isCantinaSwiper: false };

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    if (e.button !== 0) return; // solo left click
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return; // nuove tab ecc
    const a = e.target.closest('a[href]');
    if (!a) return;

    // Debug specifico per Chrome
    const isChrome = /Chrome/.test(navigator.userAgent) && /Google Inc/.test(navigator.vendor);
    if (isChrome) {
      console.log('🔍 Chrome click detected:', {
        href: a.getAttribute('href'),
        isSwiper: !!a.closest('.swiper, .swiper-container, .swiper-slide'),
        isCantinaSwiper: !!a.closest('.cantine-swiper-container, .cantine-swiper'),
        target: e.target,
        currentTarget: e.currentTarget
      });
    }

    updateNavMetaFromAnchor(a);
  });
}

// Initialize Swup
const swup = new Swup({
  ignoreVisit: (url, { el } = {}) => {
    // Escludi i link che appartengono al language switcher di WPML
    // Controlliamo sia l'elemento cliccato che i suoi genitori per classi WPML
    if (el && (
      el.closest('.header-lang-switcher') ||
      el.closest('.wpml-ls-item') ||
      el.closest('.wpml-ls-link') ||
      el.classList.contains('wpml-ls-link')
    )) {
      console.log('🌐 Swup: Link WPML rilevato - escludo dalla navigazione AJAX');
      return true; // Ignora questa visita (usa caricamento standard del browser)
    }
    return false; // Gestisci normalmente con Swup
  },
  plugins: [
    new SwupHeadPlugin(),
    new SwupBodyClassPlugin(),
    new SwupA11yPlugin({ focus: '#content', scroll: true })
  ]
});

// Expose swup globally for hamburger menu
window.swup = swup;

// Forza il reload completo quando si torna indietro con il tasto del browser,
// bypassando qualsiasi transizione gestita da Swup
swup.hooks.replace('history:popstate', (visit, { event }, defaultHandler) => {
  if (visit?.history?.popstate && visit.history.direction === 'backwards') {
    const baseUrl = visit.to?.url || (window.location.pathname + window.location.search);
    const targetUrl = baseUrl + (visit.to?.hash || '');

    if (typeof stopCursorLoading === 'function' && isCursorLoading()) {
      stopCursorLoading();
    }

    window.location.href = targetUrl;
    return;
  }

  return defaultHandler?.(visit, { event });
});

// Inizializza cantScroll solo al primo caricamento (non durante navigazioni Swup)
initCantScroll(swup);

// Swup hooks
swup.hooks.on('visit:start', () => {
  // Reset completo degli effetti di testo all'inizio della navigazione
  // Questo previene problemi con back/forward del browser
  resetTextEffects();
  //console.log('🔄 visit:start - effetti di testo resettati');
  // NON forzare scroll qui - troppo presto, visibile all'utente
  // Lo scroll avverrà durante l'animazione quando l'overlay è visibile
});

swup.hooks.on('animation:out:await', () => {
  document.body.classList.add('is-loading');
  // NON forzare scroll qui - aspettiamo che l'overlay sia visibile
});

swup.hooks.on('animation:in:await', () => {
  document.body.classList.remove('is-loading');
  // Lo scroll è già stato fatto in content:replace
});

// Overlay per transizioni Swup
function ensureTransitionOverlay(customLogo = null, customBg = null, hideLogo /* no default */, customBgImage = null) {
  // Se non hai passato nulla, riusa i parametri precedenti (ma solo se hideLogo è davvero "non definito")
  if (customOverlayParams && customLogo == null && customBg == null && typeof hideLogo === 'undefined') {
    customLogo = customOverlayParams.logo;
    customBg = customOverlayParams.bg;
    customBgImage = customOverlayParams.bgImage;
    hideLogo = typeof customOverlayParams.hideLogo === 'boolean' ? customOverlayParams.hideLogo : true;
  }

  // Se non specificato, il default è nascondere il logo
  const effectiveHideLogo = (typeof hideLogo === 'undefined') ? true : !!hideLogo;

  let overlay = document.getElementById('swup-transition-overlay');
  if (!overlay) {
    overlay = document.createElement('div');
    overlay.id = 'swup-transition-overlay';
    Object.assign(overlay.style, {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      backgroundColor: customBg || '#e5ded7',
      zIndex: 10000,
      pointerEvents: 'none',
      opacity: 0,
      display: 'none',
      transition: 'opacity 0.2s ease' // aiuta a evitare flicker
    });

    // Struttura fissa: niente innerHTML dinamico ad ogni call
    const inner = document.createElement('div');
    inner.className = 'overlay-inner';
    Object.assign(inner.style, {
      position: 'relative',
      width: '100%',
      height: '100%',
      backgroundColor: customBg || '#e5ded7',
    });
    const is_mobile = window.innerWidth < 1024;
    const logoWrap = document.createElement('div');
    logoWrap.className = 'logo-container';
    Object.assign(logoWrap.style, {
      position: 'absolute',
      top: is_mobile ? '40%' : '50%',
      left: '50%',
      transform: 'translate(-50%,-50%)',
      width: is_mobile ? '150px' : '250px',
      height: is_mobile ? '150px' : '250px',
      display: 'none', // nascosto di default
    });

    const img = document.createElement('img');
    img.alt = 'Logo overlay';
    Object.assign(img.style, {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
    });
    img.src = customLogo || '/wp-content/themes/agricole/assets/icons/agb-logo-preload.svg';

    logoWrap.appendChild(img);
    inner.appendChild(logoWrap);
    overlay.appendChild(inner);
    document.body.appendChild(overlay);
  }

  // Aggiorna solo ciò che serve
  // Se c'è un'immagine hero_bg, usala come background, altrimenti usa il colore
  if (customBgImage) {
    overlay.style.backgroundImage = `url('${customBgImage}')`;
    overlay.style.backgroundSize = 'cover';
    overlay.style.backgroundPosition = 'center center';
    overlay.style.backgroundRepeat = 'no-repeat';
    overlay.style.backgroundColor = customBg || '#e5ded7'; // Colore di fallback mentre l'immagine carica
    //console.log('🖼️ Overlay con immagine hero_bg:', customBgImage);
  } else {
    // Nessuna immagine: usa solo colore e rimuovi eventuali immagini precedenti
    overlay.style.backgroundImage = 'none';
    overlay.style.backgroundColor = customBg || '#e5ded7';
    //console.log('🎨 Overlay con colore:', customBg || '#e5ded7');
  }

  // Aggiorna anche il colore dell'overlay-inner
  const overlayInner = overlay.querySelector('.overlay-inner');
  if (overlayInner) {
    if (customBgImage) {
      overlayInner.style.backgroundImage = `url('${customBgImage}')`;
      overlayInner.style.backgroundSize = 'cover';
      overlayInner.style.backgroundPosition = 'center center';
      overlayInner.style.backgroundRepeat = 'no-repeat';
      overlayInner.style.backgroundColor = customBg || '#e5ded7';
    } else {
      overlayInner.style.backgroundImage = 'none';
      overlayInner.style.backgroundColor = customBg || '#e5ded7';
    }
  }

  const logoWrap = overlay.querySelector('.logo-container');
  const img = overlay.querySelector('.logo-container img');

  if (customLogo && img) {
    img.src = customLogo;
    //console.log('🏷️ Logo overlay impostato:', customLogo);
  }

  // Mostra/nascondi logo SENZA ricreare HTML
  logoWrap.style.display = effectiveHideLogo ? 'none' : 'block';
  //console.log('👁️ Logo display:', effectiveHideLogo ? 'nascosto' : 'visibile');

  // Salva gli ultimi parametri effettivi
  customOverlayParams = {
    logo: customLogo || (img ? img.src : null),
    bg: customBg || '#e5ded7',
    bgImage: customBgImage,
    hideLogo: effectiveHideLogo
  };

  return overlay;
}

function initCustomSelect() {
  //if (!window.gsap) { console.warn('GSAP non trovato'); return; }

  // supporta più select e la classe messa direttamente sul <select> CF7
  const selects = document.querySelectorAll('select.cantina-select[name="select-cantina"]');
  if (!selects.length) return;

  selects.forEach(select => {
    // evita doppie inizializzazioni
    if (select.dataset.enhanced === '1') return;
    select.dataset.enhanced = '1';

    // crea wrapper custom
    const wrapper = document.createElement('div');
    wrapper.className = 'custom-select';
    wrapper.innerHTML = `
      <button type="button" class="custom-select__trigger" aria-haspopup="listbox" aria-expanded="false">
        <span class="custom-select__label">${select.options[select.selectedIndex]?.text || 'Seleziona cantina'}</span>
        <span class="custom-select__chevron" aria-hidden="true"></span>
      </button>
      <ul class="custom-select__options" role="listbox"></ul>
    `;

    // popola liste
    const list = wrapper.querySelector('.custom-select__options');
    Array.from(select.options).forEach(opt => {
      const li = document.createElement('li');
      li.textContent = opt.text;
      li.dataset.value = opt.value;
      if (opt.selected) li.setAttribute('aria-selected', 'true');
      list.appendChild(li);
    });

    // inserisci e nascondi il select nativo (resta per invio/validation)
    select.style.position = 'absolute';
    select.style.width = '1px';
    select.style.height = '1px';
    select.style.padding = '0';
    select.style.margin = '-1px';
    select.style.overflow = 'hidden';
    select.style.clip = 'rect(0,0,0,0)';
    select.style.whiteSpace = 'nowrap';
    select.style.border = '0';
    select.insertAdjacentElement('afterend', wrapper);

    const trigger = wrapper.querySelector('.custom-select__trigger');
    const labelEl = wrapper.querySelector('.custom-select__label');

    // stato chiuso: overlay assoluto, niente layout shift
    Object.assign(list.style, {
      position: 'absolute',
      left: 0, right: 0, top: 'calc(100% + 8px)',
      zIndex: 50
    });
    gsap.set(list, { display: 'none', autoAlpha: 0, scaleY: 0, transformOrigin: 'top' });

    let open = false;
    const openList = () => {
      if (open) return; open = true;
      wrapper.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      gsap.timeline().set(list, { display: 'block' }).to(list, { scaleY: 1, autoAlpha: 1, duration: 0.22, ease: 'power2.out' });
    };
    const closeList = () => {
      if (!open) return; open = false;
      wrapper.classList.remove('is-open');
      trigger.setAttribute('aria-expanded', 'false');
      gsap.timeline().to(list, { scaleY: 0, autoAlpha: 0, duration: 0.18, ease: 'power2.in' }).set(list, { display: 'none' });
    };

    // click su trigger (include anche la freccina)
    trigger.addEventListener('click', (e) => {
      e.stopPropagation();
      open ? closeList() : openList();
    });

    // click sulle opzioni
    list.addEventListener('click', (e) => {
      const li = e.target.closest('li'); if (!li) return;
      // UI
      labelEl.textContent = li.textContent;
      list.querySelectorAll('[aria-selected="true"]').forEach(n => n.removeAttribute('aria-selected'));
      li.setAttribute('aria-selected', 'true');
      // sync con select CF7
      select.value = li.dataset.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
      closeList();
    });

    // chiudi con click esterno o Esc
    document.addEventListener('click', (e) => { if (!wrapper.contains(e.target)) closeList(); });
    document.addEventListener('keydown', (e) => { if (e.key === 'Escape') closeList(); });
  });
}

function reinitializeContactForm7(scope = document) {
  if (typeof window.wpcf7 === 'undefined') {
    return;
  }

  const forms = scope.querySelectorAll('.wpcf7-form');
  if (!forms.length) {
    return;
  }

  forms.forEach(form => {
    if (typeof window.wpcf7.initForm === 'function') {
      window.wpcf7.initForm(form);
    } else if (typeof window.wpcf7.init === 'function') {
      window.wpcf7.init(form);
    }

    // Reinitialize Conditional Fields plugin if present
    if (window.wpcf7cf && typeof window.wpcf7cf.initForm === 'function') {
      const $ = window.jQuery;
      const $form = ($ && typeof $.fn !== 'undefined') ? $(form) : null;
      if ($form && typeof $form.each === 'function') {
        window.wpcf7cf.initForm($form);
      } else {
        console.warn('wpcf7cf.initForm requires jQuery collection; skipped reinit for form:', form);
      }
    } else if (typeof window.wpcf7cfDomReady === 'function') {
      try {
        window.wpcf7cfDomReady(form);
      } catch (error) {
        console.warn('wpcf7cfDomReady error:', error);
      }
    }

    // Dispatch custom event to mimic initialisation lifecycle hooks
    try {
      const event = new CustomEvent('wpcf7cf:reinit', { detail: { form } });
      form.dispatchEvent(event);
    } catch (error) {
      // No-op if CustomEvent fails
    }
  });
}

// init al ready e dopo render CF7 (ajax)
document.addEventListener('wpcf7init', initCustomSelect);
document.addEventListener('wpcf7invalid', initCustomSelect);
document.addEventListener('wpcf7mailsent', initCustomSelect);
document.addEventListener('wpcf7reset', initCustomSelect);


// 1) ANIMATION OUT
swup.hooks.on('animation:out:start', () => {
  // Imposta il flag di transizione
  isTransitioning = true;

  // Note: pulizia istanze video ora gestita in content:replace per evitare duplicazioni
  // e garantire che avvenga al momento giusto per back/forward del browser

  const wrapper = document.querySelector('.wrapper-transition');
  const isFromCantina = document.querySelector('main.cantina-single') !== null;

  // INFO sul click corrente
  const nav = window._swupNav || {};
  const isToCantina = !!nav.isCantina;                 // NON leggere dal DOM corrente
  const allowCantinaTransition = !!nav.allowCantina;   // gate principale
  const isFromCantinaSwiper = !!nav.isCantinaSwiper;   // nuovo: se viene dal swiper delle cantine
  const isLanguageSwitcher = !!nav.isLanguageSwitcher; // nuovo: se è un cambio lingua WPML

  // Se è un cambio lingua WPML, NON fare transizioni cantina speciali
  if (isLanguageSwitcher) {
    console.log('🌐 [animation:out:start] Cambio lingua - skip transizione cantina speciale');
    lastTransitionId = null;
    // NON bloccare lo scroll per i cambi lingua
    return;
  }

  // BLOCCA LO SCROLL per le transizioni cantina→cantina
  if (isFromCantina && allowCantinaTransition && !isFromCantinaSwiper && isToCantina) {
    //console.log('🚫 Blocco scroll per transizione cantina→cantina');
    disableScroll();
  }

  if (wrapper && isFromCantina && allowCantinaTransition && !isFromCantinaSwiper) {
    const nextId = wrapper.dataset.nextId;
    lastTransitionId = nextId;

    // Non c'è più bisogno di gestire un logo specifico nel mirror
    // Tutti i loghi in cant-logos rimangono sempre visibili

    const space = document.getElementById(`space-transition-${nextId}`);
    if (space && space.parentNode !== document.body) document.body.appendChild(space);

    if (space) {
      // Rileva se siamo su mobile
      const isMobile = window.innerWidth <= 768;
      // Su mobile usa 84vh, su desktop mantieni 76vh
      const targetHeight = isMobile ? '82vh' : '76vh';
      // Applica offset y: -79 per risoluzioni > 2000px di larghezza o > 1190px di altezza
      const yOffset = (window.innerHeight > 1180) ? -79 : 0;
      gsap.to(space, { duration: 0.35, y: yOffset, ease: 'power1.out', height: targetHeight, delay: 1.2, backgroundSize: 'cover' });
    }
  } else {
    // Se il click viene dallo Swiper delle cantine o non è verso una cantina: nessuna transizione cantina speciale
    // Ma i link del Swiper delle cantine avranno comunque le transizioni normali
    lastTransitionId = null;
    // (continua pure con l'overlay generico se lo vuoi sempre)
  }
  // Sistema semplificato: la transizione è ora gestita solo da opacity della .next-hero-bg
  //console.log('🏷️ Transizione semplificata avviata - solo fade opacity');
  // Gestione overlay per tutte le transizioni - usa parametri personalizzati se disponibili
  let overlay;

  // Se ci sono parametri personalizzati (es. da link cantina), usali
  if (customOverlayParams) {
    //console.log('🎨 Usando parametri personalizzati per overlay:', customOverlayParams);
    overlay = ensureTransitionOverlay(
      customOverlayParams.logo,
      customOverlayParams.bg,
      customOverlayParams.hideLogo,
      customOverlayParams.bgImage // Passa anche l'immagine hero_bg
    );

    // AGGIORNA SUBITO il theme-color per iOS Liquid Glass (prima del content:replace)
    if (customOverlayParams.bg) {
      console.log('🎨 [animation:out:start] Aggiornamento theme-color con customOverlayParams.bg');
      updateThemeColor(customOverlayParams.bg, 'animation:out:start (link click)');
    }
  } else {
    // Altrimenti usa la transizione normale
    const color = getCantinaColorSafe(document);
    overlay = ensureTransitionOverlay(null, color, true, null);
  }

  overlay.style.display = 'block';
  gsap.set(overlay, { opacity: 0 });
  return new Promise(resolve => {
    gsap.to(overlay, {
      opacity: 1,
      duration: 0.2,
      ease: 'power1.out',
      onStart: () => overlay.style.pointerEvents = 'auto',
      onComplete: resolve
    });
  });
});

// 3) ANIMATION IN
swup.hooks.on('animation:in:start', () => {
  // Usa gli stessi parametri dell'overlay (già impostati in animation:out:start)
  let overlay;
  if (customOverlayParams) {
    overlay = ensureTransitionOverlay(
      customOverlayParams.logo,
      customOverlayParams.bg,
      customOverlayParams.hideLogo,
      customOverlayParams.bgImage // Passa anche l'immagine
    );
    // Nota: il theme-color è già stato aggiornato in animation:out:start
  } else {
    overlay = ensureTransitionOverlay(null, getCantinaColorSafe(document), true, null);
  }

  // Ferma l'animazione del cursor se attiva
  if (isCursorLoading()) {
    stopCursorLoading();
  }

  const isLandingOnCantina = document.querySelector('main.cantina-single') !== null;
  //const isFromNormalPage = !lastTransitionId; // True se proveniamo da una pagina normale
  const nav = window._swupNav || {};
  const isFromCantinaSwiper = !!nav.isCantinaSwiper;
  // Lo scroll in alto è già stato fatto all'inizio di content:replace
  // const isSpecialCantinaTransition = isLandingOnCantina && lastTransitionId && !isFromCantinaSwiper;

  // if (!isSpecialCantinaTransition) {
  //   console.log('📜 Transizione normale - scroll già gestito all\'inizio di content:replace');
  // }

  // Se veniamo dal Swiper delle cantine, trattiamo come transizione normale
  const baseDelay = (lastTransitionId && !isFromCantinaSwiper) ? 0 : 0.05; // Ridotto drasticamente da 0.3s a 0.05s per velocizzare molto il video

  if (isLandingOnCantina && lastTransitionId && !isFromCantinaSwiper) {
    // TRANSIZIONE CANTINA → CANTINA
    const FINAL_DELAY = getHeroLogoFinalDelay(baseDelay);

    // quando l'hero è visibile, spegni il mirror e rimettilo nel footer
    gsap.delayedCall(FINAL_DELAY + 0.01, () => { // Ridotto da 0.02 a 0.01 per velocizzare
      const mirror = document.querySelector('.cantina-hero-mirror.is-promoted');
      if (mirror) {
        gsap.to(mirror, {
          autoAlpha: 0,
          duration: 0.15, // Ridotto da 0.2 a 0.15
          onComplete: () => {
            mirror.classList.remove('is-active', 'is-promoted');
            gsap.set(mirror, { clearProps: 'transform,opacity' });
            if (mirror._ph && mirror._ph.parentNode) {
              mirror._ph.parentNode.insertBefore(mirror, mirror._ph);
              mirror._ph.remove();
              mirror._ph = null;
            } else {
              // fallback
              const wrap = document.querySelector('#site-footer .wrapper-transition');
              if (wrap) wrap.appendChild(mirror);
            }
          }
        });
      }
    });
  }

  const hero = document.querySelector('.cantina-hero');
  if (hero) {
    // Verifica l'opacity corrente per decidere se animare o impostare direttamente
    const currentOpacity = parseFloat(getComputedStyle(hero).opacity);

    if (currentOpacity < 0.5) {
      // Hero nascosto: anima verso opacity 1
      gsap.to(hero, {
        opacity: 1,
        duration: 0.3,
        ease: 'power2.out',
        onComplete: () => {
          const swupRoot = document.getElementById('swup');
          if (swupRoot) swupRoot.style.backgroundColor = '';
        }
      });
      //console.log('🏷️ animation:in:start - Hero animato da', currentOpacity, 'a 1');
    } else {
      // Hero già visibile (può succedere con back/forward): imposta direttamente
      gsap.set(hero, { opacity: 1, visibility: 'visible' });
      const swupRoot = document.getElementById('swup');
      if (swupRoot) swupRoot.style.backgroundColor = '';
      //console.log('🏷️ animation:in:start - Hero già visibile:', currentOpacity);
    }
  }
  const FINAL_DELAY = getHeroLogoFinalDelay(baseDelay);
  gsap.delayedCall(FINAL_DELAY + 0.01, () => { // Ridotto da 0.02 a 0.01 per velocizzare
    const heroMirror = document.querySelector('.cantina-hero-mirror');
    if (heroMirror) {
      gsap.to(heroMirror, {
        autoAlpha: 1,
        duration: 0.15, // Ridotto da 0.2 a 0.15
        onComplete: () => {
          heroMirror.classList.remove('is-active');
          gsap.set(heroMirror, { clearProps: 'zIndex,willChange' });
        }
      });
    }
  });

  return new Promise(resolve => {
    gsap.to(overlay, {
      opacity: 0,
      duration: 0.3, // Ridotto da 0.4 a 0.3 per il fade-out dell'overlay
      delay: baseDelay, // Il delay si basa su baseDelay
      ease: 'power2.inOut',
      onComplete: () => {
        overlay.style.pointerEvents = 'none';
        overlay.style.display = 'none';
        resolve();
      }
    });
  });
});

// Registra ScrollTrigger solo se disponibile
if (gsap && ScrollTrigger) {
  gsap.registerPlugin(ScrollTrigger);

  // Disabilita il refresh automatico di ScrollTrigger al resize per evitare conflitti
  ScrollTrigger.config({
    autoRefreshEvents: "visibilitychange,DOMContentLoaded,load"
  });
} else {
  console.warn('ScrollTrigger non disponibile');
}

// 4) CONTENT REPLACE
swup.hooks.on('content:replace', () => {
  // AGGIORNA il theme-color per iOS Liquid Glass
  // Usiamo un piccolo delay per assicurarci che SwupHeadPlugin abbia finito
  console.log('🎨 [content:replace] Inizio - scheduling aggiornamento theme-color');

  // Aggiorna subito (potrebbe essere sovrascritto da SwupHeadPlugin)
  updateThemeColorForCantina('content:replace-immediate');

  // Aggiorna di nuovo dopo un breve delay per garantire che sia l'ultimo
  setTimeout(() => {
    console.log('🎨 [content:replace] Aggiornamento theme-color (dopo delay)');
    updateThemeColorForCantina('content:replace-delayed');
  }, 10);

  // FORZA SCROLL IN ALTO SUBITO - l'overlay è visibile, il contenuto sta per essere sostituito
  // Disabilita completamente smooth scroll durante la transizione
  document.documentElement.style.scrollBehavior = 'auto';
  document.body.style.scrollBehavior = 'auto';

  // Reset della posizione salvata per le transizioni (non vogliamo ripristinare la vecchia posizione)
  scrollPosition = 0;

  // Esegui scroll istantaneo
  forceScrollToTop(true);

  // Assicurati che non siamo più nel primo caricamento
  isFirstPageLoad = false;

  // Verifica se siamo in una transizione cantina usando lastTransitionId
  const navInfo = window._swupNav || {};
  const isFromSwiperTransition = !!navInfo.isCantinaSwiper;
  const isTransitioningFromCantina = !!lastTransitionId && !isFromSwiperTransition; // Non considerare i link del swiper come transizioni cantina speciali
  const isToCantina = document.querySelector('main.cantina-single') !== null;
  const isToArchiveCantine = !!document.querySelector('.cantine-slider');

  if (isTransitioningFromCantina && isToCantina) {
    document.body.classList.add('cantina-transition');

    // nascondo subito il logo hero (sicuro contro qualsiasi "blink")
    const heroLogo = document.querySelector('.cantina-hero .cantina-logo');
    if (heroLogo) gsap.set(heroLogo, { autoAlpha: 1 });
  } else {
    // per ogni altro caso, assicuro reset
    document.body.classList.remove('cantina-transition');
  }

  // GESTIONE COMPLETA next-hero-bg in content:replace (mentre overlay è visibile)
  // Questo evita qualsiasi flash perché tutto avviene durante la sostituzione del contenuto
  const nextHeroBg = document.querySelector('.next-hero-bg');
  if (nextHeroBg && isToCantina) {
    //console.log('🎭 Reset completo next-hero-bg in content:replace');

    // CRITICO: Per transizioni scroll-based, mantieni next-hero-bg visibile
    // Il clone verrà creato in cantScroll.js onLeave, ma fino ad allora
    // next-hero-bg deve rimanere visibile per evitare flash bianco
    const isScrollBasedTransition = !!lastTransitionId && !isFromSwiperTransition;

    // 1. PRIMA: Reset della curtain a posizione coperta (CRITICO per evitare flash)
    const curtain = nextHeroBg.querySelector('.curtain-reveal');
    if (curtain) {
      curtain.style.transform = 'translateY(0%) translateZ(0)';
      curtain.style.transition = 'none'; // Reset istantaneo
      //console.log('  ✓ Curtain resettata a 0% (copre tutto)');
    }

    // 2. Rimuovi is-transitioning (se presente)
    nextHeroBg.classList.remove('is-transitioning');

    // 3. Reset z-index e opacity
    // PER TRANSAZIONI SCROLL-BASED: mantieni opacity a 1 fino a quando il clone non è pronto
    // PER ALTRE TRANSAZIONI: imposta opacity a 0
    if (isScrollBasedTransition) {
      // Per transizioni scroll-based, mantieni visibile con z-index alto temporaneo
      // Il clone in cantScroll.js prenderà il posto
      gsap.set(nextHeroBg, {
        clearProps: 'zIndex,pointerEvents',
        zIndex: 9999998, // Alto temporaneo, il clone lo sostituirà
        opacity: 1, // Mantieni visibile durante la transizione Swup
        pointerEvents: 'none'
      });
      //console.log('  ✓ next-hero-bg mantenuto visibile per transizione scroll-based');
    } else {
      // Per altre transizioni, nascondi normalmente
      gsap.set(nextHeroBg, {
        clearProps: 'zIndex,pointerEvents',
        zIndex: 1, // Z-index basso per stare sotto il contenuto
        opacity: 0, // Mantieni a 0, sarà ripristinata in cantScroll DOPO verifica curtain
        pointerEvents: 'none'
      });
      //console.log('  ✓ is-transitioning rimossa, z-index: 1, opacity: 0');
    }
    // console.log('  ✅ next-hero-bg pronta per cantScroll (curtain copre, poi opacity→1)');
  }

  const isFromNormalPage = !lastTransitionId; // True se proveniamo da una pagina normale
  // Gestione specifica per navigazione da pagina normale a cantina
  if (isFromNormalPage) {
    //console.log('🏷️ Navigazione da pagina normale a cantina');
    const heroContainer = document.querySelector('.preview-logo');
    if (heroContainer) {
      gsap.set(heroContainer, { display: 'none' });
    }
    const footerLogo = document.querySelector('.cantina-hero-mirror .preview-logo');
    if (footerLogo) {
      gsap.set(footerLogo, { display: 'block' });
    }
  }

  if (isToCantina) {
    const color = getCantinaColorSafe(document);

    // Fissa il bg della pagina mentre l'hero arriva
    document.body.style.backgroundColor = color;
    const swupRoot = document.getElementById('swup');
    if (swupRoot) swupRoot.style.backgroundColor = color;

    // Colora anche l'overlay (così la dissolvenza è sul colore giusto)
    const overlay = ensureTransitionOverlay(null, color, true, null); // true = nascondi logo overlay
    overlay.style.backgroundColor = color;

    // L'hero parte trasparente ma con lo stesso bg: niente stacchi
    const hero = document.querySelector('.cantina-hero');
    if (hero) {
      gsap.set(hero, { backgroundColor: color, opacity: 0 });
    }
  }
  // PULIZIA COMPLETA per back/forward e navigazioni Swup
  // 1. Reset effetti di testo (già chiamato in visit:start, ma doppia sicurezza)
  resetTextEffects();

  // 2. Pulizia istanze video PRIMA di inizializzare la nuova pagina
  if (window.ScrollMapInstances && window.ScrollMapInstances.length > 0) {
    //console.log('📍 Pulizia istanze ScrollMapVideo in content:replace');
    window.ScrollMapInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    window.ScrollMapInstances = [];
  }

  if (window.SimpleScrollVideoInstances && window.SimpleScrollVideoInstances.length > 0) {
    //console.log('📍 Pulizia istanze SimpleScrollVideo in content:replace');
    window.SimpleScrollVideoInstances.forEach(instance => {
      if (instance && typeof instance.destroy === 'function') {
        instance.destroy();
      }
    });
    window.SimpleScrollVideoInstances = [];
  }

  // Pulizia animazioni parallax
  destroyAllImageParallaxWidgets();
  destroyAllCardParallaxWidgets();

  // 3. Kill ScrollTriggers MA non durante una transizione cantina attiva, ECCETTO quando andiamo all'archivio cantine
  if (!isTransitioningFromCantina || isToArchiveCantine) {
    if (window.ScrollTrigger && ScrollTrigger.getAll) {
      ScrollTrigger.getAll().forEach(t => t.kill());
    }
  }

  // Pulisci lo stato del menu
  const menuContainer = document.querySelector('#site-header .container');
  if (menuContainer) {
    menuContainer.classList.remove('is-scrolling');
  }

  // 1) Scope: il contenitore swup
  const scope = document.querySelector('#swup');
  const $scope = window.jQuery ? jQuery(scope) : null;

  // Gestione specifica per le cantine
  if (isToCantina) {
    const wrapper = document.querySelector('.wrapper-transition');
    const nextId = wrapper?.dataset?.nextId;

    if (nextId) {
      const hero = document.querySelector('.cantina-hero');

      // Recupera il colore della cantina ATTUALE
      let currentCantinaColor = getComputedStyle(document.body).getPropertyValue('--cantina-color').trim();

      if (!currentCantinaColor) {
        // Prova anche da .cantina-hero se non è sul body
        if (hero) {
          currentCantinaColor = getComputedStyle(hero).getPropertyValue('--cantina-color').trim();
        }
      }

      // Fallback di sicurezza
      if (!currentCantinaColor) {
        currentCantinaColor = '#c7c0b7';
      }

      // Applica il colore corretto solo come sfondo
      if (hero) {
        gsap.set(hero, {
          backgroundColor: currentCantinaColor,
          //opacity: 0 // opzionale, se vuoi fade-in
        });
      }
    }
    // 2) Riposiziona lo space-transition corretto
    if (wrapper) {
      const nextId = wrapper.dataset.nextId;
      const space = document.getElementById(`space-transition-${nextId}`);
      if (space && space.parentNode !== wrapper) {
        wrapper.appendChild(space);

        // Imposta opacity 0 per gli elementi desiderati
        const progressBarEl = document.querySelector('.scroll-progress');
        const progressTitleEl = document.querySelector('.infinite-preview h4');
        if (progressBarEl) progressBarEl.style.opacity = '0';
        if (progressTitleEl) progressTitleEl.style.opacity = '0';

        // GESTIONE VISIBILITÀ LOGO DELLA CANTINA NELL'HERO PRINCIPALE
        handleCantinaLogoVisibility(isTransitioningFromCantina);

        // Scroll smooth è sempre attivo, MA NON durante le transizioni cantina→cantina
        if (!isTransitioningFromCantina) {
          enableScroll();
        }
      }
    }
  } else {
    // Se non stiamo navigando verso una cantina, rimuovi tutti gli space-transition
    document.querySelectorAll('.space-transition').forEach(space => {
      space.remove();
    });
    // Se stiamo navigando via da una cantina verso una pagina normale, assicurati che il logo sia visibile nella prossima cantina che visiteremo
    // if (isTransitioningFromCantina) {
    //   // Reset del flag per le prossime navigazioni
    //   console.log('Navigazione da cantina a pagina normale - reset flag transizione');
    // }
  }

  // 3) Re-inizializza Elementor (vanilla)
  if (window.elementorFrontend) {
    if (typeof window.elementorFrontend.init === 'function') {
      window.elementorFrontend.init();
    }
    if (window.elementorFrontend.hooks && $scope) {
      window.elementorFrontend.hooks.doAction('frontend/element_ready/global', $scope);
      window.elementorFrontend.hooks.doAction('frontend/element_ready/widget', $scope);
    }
  }

  // Reinizializza il video per la nuova pagina
  setupVideoForMobile();

  // 4) Le tue custom init
  initMenu();
  initHamburgerMenu();
  initContactModal();
  initCursor();
  handleUniversalCursorLoading(); // Reinizializza il cursor loading universale (priorità massima)
  handleCantineLinks(); // Reinizializza la gestione dei link delle cantine
  handleChromeSwipersLinks(); // Reinizializza il fix specifico per Chrome
  handleOtherInternalLinks(); // Reinizializza la gestione degli altri link interni
  handleAnchorSecClick(); // Reinizializza la gestione dei click su #anchorSec
  reinitializeContactForm7();
  initCustomSelect();
  initBackToTop(); // Reinizializza il pulsante Back to top
  // Delay per gli effetti di testo nelle navigazioni Swup
  // Aumentato il delay per dare tempo al browser di ricalcolare il layout dopo il cleanup
  setTimeout(() => {
    // Forza un reflow prima di ri-inizializzare gli effetti
    void document.body.offsetHeight;
    initTextEffects(0); // Re-initialize text effects for new content
  }, 150); // Aumentato da 100ms a 150ms per dare più tempo al layout
  setTimeout(() => {
    initArchiveCantine();
  }, 100);
  initCantineSwiper();
  initGallerySwiper();
  gestisciFloatingLabels();
  initFooterMarquee();
  initGroupSwiper();

  try {
    // PULIZIA COMPLETA di tutti i widget parallax esistenti
    destroyAllParallaxWidgets();

    // Kill TUTTI gli ScrollTrigger relativi ai widget parallax (pulizia aggiuntiva)
    if (gsap?.ScrollTrigger?.getAll) {
      gsap.ScrollTrigger.getAll().forEach(t => {
        if (t.trigger?.matches?.('[data-widget-id^="home-parallax-"]') ||
          t.trigger?.matches?.('[data-parallax-speed]')) {
          t.kill();
        }
      });
    }

    // Re-inizializza tutti i widget parallax presenti nella nuova pagina
    //console.log('🔄 Re-inizializzazione widget parallax dopo transizione Swup');

    // Delay per assicurarsi che il DOM sia completamente pronto
    setTimeout(() => {
      initAllParallaxWidgets();

      // Refresh aggiuntivo dopo l'inizializzazione
      setTimeout(() => {
        if (gsap && gsap.ScrollTrigger) {
          gsap.ScrollTrigger.refresh();
          console.log('🔄 ScrollTrigger refresh aggiuntivo dopo inizializzazione parallax');
        }
      }, 100);
    }, 100);

    // Refresh ScrollTrigger con delay per assicurarsi che il DOM sia pronto
    setTimeout(() => {
      if (gsap && gsap.ScrollTrigger) {
        gsap.ScrollTrigger.refresh();
        //console.log('🔄 ScrollTrigger refresh completato per tutti i parallax');
      }
    }, 150); // Aumentato da 100 a 150ms

    // Refresh aggiuntivo per sicurezza
    requestAnimationFrame(() => {
      if (gsap && gsap.ScrollTrigger) {
        gsap.ScrollTrigger.refresh();
      }
    });

    // Refresh finale al load delle immagini
    window.addEventListener('load', () => {
      if (gsap && gsap.ScrollTrigger) {
        gsap.ScrollTrigger.refresh();
        //console.log('🔄 ScrollTrigger refresh finale al load');
      }
    }, { once: true });
  } catch (e) {
    console.warn('[Parallax] re-init error:', e);
  }

  // Gestisci il logo mirror anche dopo le navigazioni Swup
  initCantinaHeroMirror();

  // Inizializza il colore dei loghi delle cantine anche dopo le navigazioni
  initCantinaLogosColor();

  // Aggiorna il theme color per le barre del browser (iOS Liquid Glass)
  updateThemeColorForCantina('content:replace-end');


  // 6) Infinite scroll - Gestione cantScroll con controllo auto-scroll
  const navState = window._swupNav || {};
  const isComingFromSwiper = !!navState.isCantinaSwiper;
  const isLanguageSwitch = !!navState.isLanguageSwitcher; // Nuovo: rileva cambio lingua WPML

  //console.log('🔄 Gestione cantScroll:', { isTransitioningFromCantina, isToArchiveCantine, isComingFromSwiper, isLanguageSwitch });

  // Se è un cambio lingua WPML, NON eseguire transizioni cantina speciali
  // Questo evita il loop infinito tra cantine
  if (isLanguageSwitch) {
    console.log('🌐 Cambio lingua rilevato - skip transizioni cantina speciali');
    // Forza scroll in alto per sicurezza
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;

    // Disabilita eventuali ScrollTrigger esistenti di cantScroll
    if (typeof disableCantScroll === 'function') {
      disableCantScroll();
    }

    // Inizializza cantScroll con un delay maggiore per evitare trigger immediati
    setTimeout(() => {
      // Ri-forza scroll in alto prima di inizializzare
      window.scrollTo(0, 0);
      initCantScroll(swup);
      if (window.ScrollTrigger && ScrollTrigger.refresh) {
        ScrollTrigger.refresh();
      }
    }, 500);
  } else if (isComingFromSwiper) {
    // Se arriviamo dal Swiper, disabilita l'auto-scroll
    //console.log('🎨 Arrivo dal Swiper - disabilito auto-scroll');
    setDisableAutoScroll(true);
    initCantScroll(swup);
  } else if (isTransitioningFromCantina && !isToArchiveCantine) {
    // Se non arriviamo dal Swiper, abilita l'auto-scroll
    setDisableAutoScroll(false);
    // Aspetta che le animazioni di cantScroll finiscano
    setTimeout(() => {
      initCantScroll(swup);
      if (window.ScrollTrigger && ScrollTrigger.refresh) {
        ScrollTrigger.refresh();
      }
    }, 800); // 800ms per dare tempo alle animazioni di finire
  } else {
    // Inizializza cantScroll per tutte le altre situazioni
    setDisableAutoScroll(false);
    initCantScroll(swup);
  }

  // Se arriviamo dal Swiper, gestisci lo scroll
  if (isComingFromSwiper) {
    // Lo scroll in alto è già stato fatto all'inizio di content:replace
    setTimeout(() => {
      enableScroll();
      //console.log('🔄 Scroll riattivato dopo arrivo dal Swiper');
    }, 200);
  }

  // Lo scroll in alto è già stato fatto all'inizio di content:replace
  // if (isTransitioningFromCantina || isComingFromSwiper) {
  //   console.log('📜 Transizione - scroll già gestito all\'inizio di content:replace');
  // }

  // ripristina e anima i pin
  // Aspetta che il DOM sia completamente pronto prima di cercare i pin
  setTimeout(() => {
    const pinsContainer = document.querySelector('.pins-container');

    if (pinsContainer) {
      //console.log('📍 Trovato pins-container in content:replace - re-inizializzazione pins');
      // Rimuovi eventuali pin esistenti prima di ri-crearli
      pinsContainer.innerHTML = '';

      // Usa waitForPinsData() invece di controllare immediatamente
      waitForPinsData(true); // Indica che è una navigazione Swup (incluso back/forward)
    }
  }, 200); // Aumenta il ritardo per essere sicuri che il DOM sia pronto

  // 5) Refresh GSAP ScrollTrigger - Solo se non stiamo transitando DA cantina, ECCETTO se andiamo all'archivio cantine
  if (!isTransitioningFromCantina || isToArchiveCantine) {
    //console.log('🔄 Refreshing ScrollTrigger');
    if (window.ScrollTrigger && ScrollTrigger.refresh) {
      ScrollTrigger.refresh();
    }
  }

  const content = document.querySelector('#swup');
  const scrollY = window.scrollY || window.pageYOffset;
  if (content && menuContainer && scrollY > (content.offsetTop + 50)) {
    menuContainer.classList.add('is-scrolling');
  }

  initGalleryScroll();

  // 7) Focus modal
  initFocusModal();

  // 8) Re-inizializza ScrollMapVideo/SimpleScrollVideo se presente nella nuova pagina
  // Note: le istanze precedenti sono già state distrutte all'inizio di content:replace
  // Safari ha bisogno di più tempo per caricare i video durante le transizioni
  const isSafariSwup = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
  const swupVideoDelay = isSafariSwup ? 500 : 300;

  setTimeout(() => {
    const scrollMapSections = document.querySelectorAll('[data-scrollmap]');
    if (scrollMapSections.length > 0) {
      //console.log('📍 ScrollMapVideo: trovate', scrollMapSections.length, 'sezioni da inizializzare');
      // Re-esegui gli script inline dello shortcode per inizializzare il video
      reinitScrollMapVideo();

      // Re-inizializza anche SimpleScrollVideo se necessario
      initSimpleScrollVideoSections();
    }
    // else {
    //   console.log('📍 Nessuna sezione ScrollMapVideo trovata nella nuova pagina');
    // }
  }, swupVideoDelay); // Delay maggiore per Safari (500ms) vs altri browser (300ms)
});

// 2) VISIT END
swup.hooks.on('visit:end', () => {
  // Ripristina il background-color dell'header rimuovendo gli stili inline
  // Questo permette al CSS di applicare il colore corretto in base alla classe .is-scrolling
  const siteHeader = document.querySelector('#site-header .container');
  if (siteHeader) {
    gsap.set(siteHeader, { clearProps: 'backgroundColor' });
  }

  // Pulisci il background solo se **non** sei su una cantina
  const isCantinePageNow = document.querySelector('main.cantina-single') !== null;
  if (!isCantinePageNow) {
    document.body.style.backgroundColor = '';
    const swupRoot = document.getElementById('swup');
    if (swupRoot) swupRoot.style.backgroundColor = '';
  } else {
    // Se siamo su una pagina cantina, verifica che l'hero sia visibile
    // (controllo di sicurezza finale per back/forward)
    const hero = document.querySelector('.cantina-hero');
    if (hero) {
      const currentOpacity = parseFloat(getComputedStyle(hero).opacity);
      if (currentOpacity < 0.5) {
        console.warn('⚠️ visit:end: Hero cantina con opacity bassa, ripristino a 1');
        gsap.set(hero, { opacity: 1, visibility: 'visible' });
      }
    }
  }
  // Gestione specifica per le cantine - SOLO per pagine cantina
  const isFromCantina = document.querySelector('main.cantina-single') !== null;
  // const isCantinePage = document.querySelector('main.cantina-single') !== null;

  if (isFromCantina && lastTransitionId) {
    const space = document.getElementById(`space-transition-${lastTransitionId}`);
    gsap.to(space, {
      opacity: 0,
      duration: 0.3,
      ease: 'power1.out',
      // Rimuovo lo scroll verso l'alto qui perché ora avviene all'inizio della transizione
    });
    if (space) {
      const fallbackMs = 1000; // safety
      const delayMs = (typeof window._spaceRemoveDelayMs === 'number')
        ? Math.max(fallbackMs, window._spaceRemoveDelayMs)
        : fallbackMs;

      setTimeout(() => {
        space.remove();
        window._spaceRemoveDelayMs = undefined; // cleanup
      }, delayMs);

      // enableScroll() rimosso - ora gestito con delay di 1 secondo più in basso
    }

    // reset progress bar
    const progressBar = document.querySelector('.scroll-progress-bar');
    if (progressBar) {
      gsap.set(progressBar, { width: '0%' });
    }

    // lastTransitionId viene resettato più in basso dopo i controlli finali

    // next-hero-bg è già gestito completamente in content:replace
    // Non serve fare nulla qui - evita duplicazioni e possibili flash
  }

  // Assicurati che l'overlay sia nascosto per tutte le transizioni
  const overlay = document.getElementById('swup-transition-overlay');
  if (overlay) {
    overlay.style.display = 'none';
    overlay.style.pointerEvents = 'none';
    gsap.set(overlay, { opacity: 0 });
  }

  // Pulisci eventuali cloni precedenti (SOLO fallback di sicurezza)
  // SOLO per pagine cantina
  const isCantinePageForClone = document.querySelector('main.cantina-single') !== null;
  const oldClone = document.getElementById('next-hero-bg-transition-clone');
  if (oldClone && isCantinePageForClone) {
    // Fallback di sicurezza: se il clone esiste ancora qui, nascondilo e rimuovilo
    // Ma mantienilo visibile con z-index alto fino al fade out
    gsap.set(oldClone, {
      zIndex: 99999, // ALTO per coprire tutto durante il fade out
      pointerEvents: 'none'
    });

    gsap.to(oldClone, {
      opacity: 0,
      duration: 0.05, // Più lento per una transizione più smooth
      delay: 0, // Delay per assicurarsi che la nuova pagina inizi a renderizzare
      ease: 'power1.out',
      onComplete: () => {
        oldClone.remove();
        //console.log('🏷️ Rimozione clone fallback completata');
      }
    });
  }

  // Reset e aggiorna .next-hero-bg per la prossima transizione con delay
  // SOLO per pagine cantina
  const nextHeroBg = document.querySelector('.next-hero-bg');
  if (nextHeroBg && isCantinePageForClone) {
    //console.log('🏷️ Preparazione reset .next-hero-bg');

    // Ritarda il reset per permettere al clone di rimanere visibile durante la transizione
    setTimeout(() => {
      // Forza z-index basso PRIMA dell'animazione
      gsap.set(nextHeroBg, {
        zIndex: 99998, // Molto basso per evitare sovrapposizioni
        pointerEvents: 'none'
      });

      // Reset opacity a 0 con dissolvenza graduale
      gsap.to(nextHeroBg, {
        opacity: 0,
        duration: 0.3,
        ease: 'power1.out',
        onComplete: () => {
          // Aggiorna l'immagine con quella della cantina successiva della nuova pagina
          const newSpaceTransition = document.querySelector('.space-transition');
          if (newSpaceTransition) {
            const newNextHeroBg = newSpaceTransition.dataset.nextHeroBg;
            if (newNextHeroBg && newNextHeroBg.trim() !== '') {
              nextHeroBg.style.backgroundImage = `url('${newNextHeroBg}')`;
              //console.log('🏷️ .next-hero-bg aggiornato con nuova immagine:', newNextHeroBg);
              // Ripristina z-index e RESETTA LA CURTAIN PRIMA DI RIPRISTINARE L'OPACITY
              try {
                // 1. Reset della curtain PRIMA (copre tutto)
                const curtain = nextHeroBg.querySelector('.curtain-reveal');
                if (curtain) {
                  curtain.style.transform = 'translateY(0%) translateZ(0)';
                  //console.log('🏷️ .curtain-reveal resettata alla posizione iniziale PRIMA del ripristino opacity');
                }
                // 2. SOLO DOPO aver resettato la curtain, ripristina opacity
                // MA: teniamo opacity a 0 per evitare flash - sarà ripristinata in cantScroll
                gsap.set(nextHeroBg, {
                  clearProps: 'zIndex,pointerEvents',
                  zIndex: 99995, // Ripristina z-index corretto per la prossima transizione
                  opacity: 0,    // Mantieni a 0 per ora - sarà ripristinata in cantScroll
                  pointerEvents: 'none'
                });
                //console.log('🏷️ .next-hero-bg z-index ripristinato, opacity a 0 (sarà 1 in cantScroll)');
              } catch (error) {
                console.warn('🏷️ Errore nel ripristino .next-hero-bg (aggiornamento immagine):', error);
              }
            }
          }
        }
      });
    }, 200); // Aumentato il delay a 2 secondi per assicurarsi che la transizione sia completamente finita
  }

  // Reset dei parametri personalizzati dell'overlay
  customOverlayParams = null;
  window.customOverlayParams = null;

  // Controllo di sicurezza: ferma il cursor loading se ancora attivo
  if (isCursorLoading()) {
    stopCursorLoading();
  }

  // Lo scroll in alto è già stato fatto in content:replace
  // Non serve un controllo finale qui
  const finalNav = window._swupNav || {};
  const isCurrentPageCantina = document.querySelector('main.cantina-single') !== null;
  const wasCantinaToCantina = lastTransitionId && !finalNav.isCantinaSwiper && isCurrentPageCantina;

  // if (!wasCantinaToCantina) {
  //   console.log('📜 Scroll già gestito in content:replace - nessun controllo finale necessario');
  // }

  // Reset della visibilità del logo per navigazioni future (solo se non stiamo più transitando da una cantina)
  if (!lastTransitionId) {
    // Ripristina il logo normale solo se non siamo su una pagina cantina o se stiamo navigando da una pagina non-cantina
    const isCantinePage = document.querySelector('main.cantina-single') !== null;
    if (!isCantinePage) {
      handleCantinaLogoVisibility(false, false); // false = non da cantina, false = non animato
    }
  }

  // Nascondi e rimuovi il clone con fade out - SOLO per pagine cantina
  const isCantinaForFinalCleanup = document.querySelector('main.cantina-single') !== null;
  const nextHeroBgClone = document.querySelector('.next-hero-bg-transition-clone');
  if (nextHeroBgClone && isCantinaForFinalCleanup) {
    // NON rimuovere immediatamente - prima fade out
    gsap.to(nextHeroBgClone, {
      opacity: 0,
      duration: 0.1,
      ease: 'power1.out',
      delay: 0.2, // Piccolo delay per assicurarsi che la nuova pagina sia visibile
      onComplete: () => {
        nextHeroBgClone.remove();
        //console.log('🏷️ Clone nextHeroBg rimosso con fade out alla fine della transizione');
      }
    });
  }

  // Rimuovi anche eventuali pin-spacer vuoti residui dopo un breve delay
  setTimeout(() => {
    const pinSpacers = document.querySelectorAll('.pin-spacer');
    pinSpacers.forEach((pinSpacer, index) => {
      // Se il pin-spacer è vuoto (senza figli), rimuovilo
      if (pinSpacer && pinSpacer.children.length === 0) {
        //console.log(`Rimozione pin-spacer vuoto ${index}:`, pinSpacer);
        pinSpacer.remove();
      }
    });
  }, 50); // Delay per permettere alla dissolvenza dei hero-bg di completarsi

  // RIATTIVA LO SCROLL alla fine della transizione
  // Reset del flag di transizione
  isTransitioning = false;

  // SEMPRE riattiva lo scroll alla fine della transizione
  setTimeout(() => {
    if (window.enableScroll) {
      window.enableScroll();
      console.log('🔄 Scroll riattivato dopo transizione (visit:end)');
    }
    // Assicurati che la pagina sia in alto
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
  }, 100);

  // Reset di lastTransitionId alla fine
  lastTransitionId = null;

  window._swupNav = { href: null, insideSwiper: false, isCantina: false, allowCantina: false, isCantinaSwiper: false };
});

// Aggiorna il colore delle barre browser (iOS Safari Liquid Glass, Android Chrome, etc.)
function updateThemeColor(customColor = null, caller = 'unknown') {
  // DEBUG: Log chiamata
  //console.log(`🎨 [updateThemeColor] Chiamata da: ${caller}, customColor: ${customColor}`);

  // Determina il colore da usare:
  // 1. customColor passato come parametro
  // 2. data-theme-color sul body
  // 3. --cantina-color se siamo su una pagina cantina
  // 4. fallback al colore default
  let color = customColor;
  let colorSource = 'customColor';

  if (!color) {
    color = document.body.getAttribute('data-theme-color');
    colorSource = 'data-theme-color';
  }

  if (!color) {
    // Prova a recuperare il colore della cantina corrente
    const cantinaColor = getComputedStyle(document.body).getPropertyValue('--cantina-color').trim();
    if (cantinaColor) {
      color = cantinaColor;
      colorSource = '--cantina-color';
    }
  }

  // Fallback al colore default
  if (!color) {
    color = '#c7c0b7';
    colorSource = 'fallback';
  }

  //console.log(`🎨 [updateThemeColor] Colore finale: ${color} (source: ${colorSource})`);

  // Aggiorna la custom property CSS
  document.documentElement.style.setProperty('--theme-color', color);

  // Aggiorna TUTTI i meta tag theme-color (light e dark mode)
  const themeColorMetas = document.querySelectorAll('meta[name="theme-color"]');

  //console.log(`🎨 [updateThemeColor] Meta tag trovati: ${themeColorMetas.length}`);

  if (themeColorMetas.length === 0) {
    // Se non esistono meta tag, creali (per light e dark mode)
    //console.log(`🎨 [updateThemeColor] Creazione nuovi meta tag`);
    const metaLight = document.createElement('meta');
    metaLight.name = 'theme-color';
    metaLight.setAttribute('media', '(prefers-color-scheme: light)');
    metaLight.setAttribute('content', color);
    document.head.appendChild(metaLight);

    const metaDark = document.createElement('meta');
    metaDark.name = 'theme-color';
    metaDark.setAttribute('media', '(prefers-color-scheme: dark)');
    metaDark.setAttribute('content', color);
    document.head.appendChild(metaDark);
  } else {
    // Aggiorna tutti i meta tag esistenti
    themeColorMetas.forEach((meta, index) => {
      const oldContent = meta.getAttribute('content');
      meta.setAttribute('content', color);
      //console.log(`🎨 [updateThemeColor] Meta[${index}] aggiornato: ${oldContent} → ${color}`);
    });
  }

  // Per iOS Safari: tecnica aggressiva per forzare il refresh
  // Rimuovi e ricrea TUTTI i meta tag theme-color
  const isSafari = isIOSSafari();
  //console.log(`🎨 [updateThemeColor] iOS Safari: ${isSafari}`);

  if (isSafari) {
    //console.log(`🎨 [updateThemeColor] Forzando refresh AGGRESSIVO per iOS Safari`);

    // Tecnica 1: Rimuovi tutti i meta tag esistenti e ricreali
    const existingMetas = document.querySelectorAll('meta[name="theme-color"]');
    existingMetas.forEach(meta => meta.remove());
    //console.log(`🎨 [updateThemeColor] Rimossi ${existingMetas.length} meta tag`);

    // Forza reflow
    void document.head.offsetHeight;

    // Ricrea i meta tag con il nuovo colore
    const metaLight = document.createElement('meta');
    metaLight.name = 'theme-color';
    metaLight.setAttribute('media', '(prefers-color-scheme: light)');
    metaLight.setAttribute('content', color);
    document.head.appendChild(metaLight);

    const metaDark = document.createElement('meta');
    metaDark.name = 'theme-color';
    metaDark.setAttribute('media', '(prefers-color-scheme: dark)');
    metaDark.setAttribute('content', color);
    document.head.appendChild(metaDark);

    //console.log(`🎨 [updateThemeColor] Ricreati 2 meta tag con colore: ${color}`);

    // Tecnica 2: Forza Safari a rileggere con delay multipli
    requestAnimationFrame(() => {
      // Aggiorna di nuovo dopo il primo frame
      metaLight.setAttribute('content', color);
      metaDark.setAttribute('content', color);

      setTimeout(() => {
        // E ancora dopo 50ms
        metaLight.setAttribute('content', color);
        metaDark.setAttribute('content', color);
        //console.log(`🎨 [updateThemeColor] Refresh iOS Safari completato (50ms)`);
      }, 50);

      setTimeout(() => {
        // E ancora dopo 100ms per sicurezza
        metaLight.setAttribute('content', color);
        metaDark.setAttribute('content', color);
        //console.log(`🎨 [updateThemeColor] Refresh iOS Safari completato (100ms)`);
      }, 100);
    });
  }
  //console.log(`🎨 [updateThemeColor] ✅ Completato - colore: ${color}`);
}

// Helper per rilevare iOS Safari
function isIOSSafari() {
  const ua = navigator.userAgent;
  const iOS = /iPad|iPhone|iPod/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  const webkit = /WebKit/.test(ua);
  const notChrome = !/CriOS/.test(ua) && !/Chrome/.test(ua);
  return iOS && webkit && notChrome;
}

// Aggiorna il theme color quando cambia la cantina o la pagina
function updateThemeColorForCantina(caller = 'updateThemeColorForCantina') {
  //console.log(`🎨 [updateThemeColorForCantina] Chiamata da: ${caller}`);

  const mainEl = document.querySelector('main.cantina-single');
  //console.log(`🎨 [updateThemeColorForCantina] Pagina cantina: ${!!mainEl}`);

  if (mainEl) {
    // Recupera il colore della cantina
    let cantinaColor = getComputedStyle(document.body).getPropertyValue('--cantina-color').trim();
    //console.log(`🎨 [updateThemeColorForCantina] --cantina-color da body: "${cantinaColor}"`);

    if (!cantinaColor) {
      cantinaColor = getComputedStyle(mainEl).getPropertyValue('--cantina-color').trim();
      //console.log(`🎨 [updateThemeColorForCantina] --cantina-color da main: "${cantinaColor}"`);
    }
    if (cantinaColor) {
      updateThemeColor(cantinaColor, `${caller} (cantina)`);
      return;
    }
  }

  // Se non siamo su una cantina, usa il colore default
  updateThemeColor(null, `${caller} (default)`);
}

// Esponi le funzioni globalmente
window.updateThemeColor = updateThemeColor;
window.updateThemeColorForCantina = updateThemeColorForCantina;

// Funzione helper per debug - forzare il re-rendering dei pin
window.forceRenderPins = function () {
  const pinsContainer = document.querySelector('.pins-container');
  if (pinsContainer && typeof pinsData !== 'undefined' && Array.isArray(pinsData)) {
    pinsContainer.innerHTML = '';
    renderCantinePins(pinsData);
    animatePinsWithDelay();
  }
};